/**
 * Program Audits — the internal audit workspace, run exactly like an FDR
 * assessment: one engagement per audit cycle, Overview | Fieldwork |
 * Correspondence | Results. ODAG first; CDAG, FA, and SNPCC join the same
 * board later.
 *
 * How an audit runs here: universes land in their named slots and are
 * tested with the Precision Scrubber (timeliness and data accuracy live
 * there; failures become IDS conditions). Case testing then runs on the
 * Precision program tool: rows are sampled cases, the questions come from
 * the protocol, each case ends Verified or Questioned, Compliant or
 * Condition. Correspondence keeps every email and file. Close computes the
 * results and erases universes, sample files, and case identifiers with a
 * destruction certificate.
 *
 * No vendor, no portal, no question sets sent out. Follow-ups go from Workstream.
 */
import React, { useCallback, useEffect, useState } from 'react';
import AuditCharting, { ChartGuard } from './AuditCharting';

import SentEmailView from '../FDR/SentEmailView';
import EmailReplyComposer from '../../components/EmailReplyComposer';
import { PdaCharts, PdaSetupCard, PdaListingFields, PdaListingRecord, PdaAttempts, PdaCaseBadge, PdaQualitativeForm } from './PdaPanels';
import { ResponsesTab, findingsOf, ClassificationButtons, UniverseTestControls } from './InternalPanels';
import AuditRecordPanel from './AuditRecordPanel.jsx';
import LetterEditor from './LetterEditor.jsx';
import RadvWorkspace from './RadvWorkspace.jsx';
import { AUDIT_CLASS, classOf } from './labels.js';

const API = process.env.REACT_APP_API_URL;

// The kind of audit (CMS, internal, FDR), as the chip every card and
// workspace header carries. The vocabulary lives in labels.js.
export function AuditClassChip({ audit, className = '' }) {
  const k = classOf(audit);
  return <span title={AUDIT_CLASS[k].blurb} className={`inline-flex items-center px-1.5 py-px rounded-full text-[10px] font-bold uppercase tracking-wider ${AUDIT_CLASS[k].chip} ${className}`}>{AUDIT_CLASS[k].short}</span>;
}

async function jfetch(path, opts = {}) {
  const r = await fetch(`${API}/api/fdr${path}`, { credentials: 'include', ...opts });
  const d = await r.json().catch(() => ({}));
  if (!r.ok || d.success === false) { const e = new Error(d.message || 'Request failed'); e.data = d; throw e; }
  return d;
}

// The notice or report as Word comes from the server: the ONE letter
// renderer that also builds the email attachment, so the file downloaded here
// is byte-for-byte the file the recipients received (client letterhead,
// brand color, headings, page footer).
async function downloadLetterDocx(auditId, text, title) {
  const r = await fetch(`${API}/api/fdr/internal-audits/${auditId}/document.docx`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, title }),
  });
  if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.message || 'Could not build the Word document'); }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${title}.docx`; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

// Each area tab draws its cases from ONE universe; Data Integrity spans all six.
const TAB_UNIVERSE = {
  'UM (OD)': 'OD', 'Appeals (RECON)': 'RECON', 'Effectuations (EFF_C)': 'EFF_C',
  'Claims (PYMT_C)': 'PYMT_C', 'Grievances (GRV_C)': 'GRV_C', 'AIP': 'AIP',
  'Coverage Determinations (CD)': 'CD', 'Exceptions (CDER)': 'CDER', 'Claims (PYMT_D)': 'PYMT_D',
  'Appeals (RD)': 'RD', 'Effectuations (EFF_D)': 'EFF_D', 'Grievances (GRV_D)': 'GRV_D', 'Drug Management (CARA)': 'AR',
  'Care Coordination (SNPE)': 'SNPE',
  // Protocol element tabs that draw from one universe (the multi universe
  // elements, integrity testing, processing and classification, keep the
  // universe picker).
  'Drug Management Program': 'AR', 'Care Coordination': 'SNPE',
  // Legacy PDA tabs, kept so audits already carrying work still read.
  'Locations and Phones (F1)': 'PDL', 'Hospital Affiliations (F2)': 'PDL',
  'Accepting New Patients (F3)': 'PDL', 'Network Awareness (F4)': 'PDL',
};

// Tabs that have NO universe at all. A directory audit samples straight from
// the published online directory, so asking which universe the sample came
// from is meaningless, and offering the ODAG universes on it is worse
// (operator order 2026-08-27).
const NO_UNIVERSE_TABS = new Set(['Provider Call Log', 'Plan Documents', 'Website Content', 'General Requirements', 'Directories and Formulary']);
// Website audit rows are plans, pages, sites and tools, named as such.
const WEBSITE_ROW = { 'Plan Documents': ['plan', 'Add a plan', 'Plan name', 'Contract and PBP · county'], 'Website Content': ['page', 'Add a page', 'Page name', 'URL'], 'General Requirements': ['site', 'Add a site', 'Website or section', 'Base URL'], 'Directories and Formulary': ['tool', 'Add a tool', 'Online tool', 'URL'] };
// Website audit (operator order 2026-10-30): on these two tabs the name and
// the link are recorded on each question, optional, not once on the row.
const QUESTION_REF_TABS = new Set(['Website Content', 'General Requirements']);

// Timeliness by measure, from the Precision Scrubber. A universe table holds
// several CMS timeliness measures (Table 1: 1.1 Standard Non-Part B, 1.2
// Standard Part B, 1.3 Expedited Non-Part B, 1.4 Expedited Part B). Every
// measure is shown on its own line against the org's COA benchmark; the
// total is labeled as the total. Nothing blended, nothing hidden. The
// universe-level result the auditor records lives on the row above, apart.
function UniverseMeasures({ sr }) {
  const sc = sr && sr.scores;
  const bm = sc && sc.benchmark;
  const rowsRaw = sc ? sc.by_scenario : (Array.isArray(sr?.timeliness?.by_scenario)
    ? sr.timeliness.by_scenario.map((x) => ({ scenario: x.scenario, tested: x.tested, untimely: x.untimely, timely: x.tested - x.untimely, rate: x.tested ? Math.round(((x.tested - x.untimely) / x.tested) * 1000) / 10 : null, benchmark: null, met: null }))
    : []);
  const rows = rowsRaw.slice().sort((a, z) => String(a.scenario).localeCompare(String(z.scenario), undefined, { numeric: true }));
  const overall = sc ? sc.overall : (sr?.timeliness ? { tested: sr.timeliness.tested, untimely: sr.timeliness.untimely, timely: sr.timeliness.tested - sr.timeliness.untimely, rate: sr.timeliness.tested ? Math.round(((sr.timeliness.tested - sr.timeliness.untimely) / sr.timeliness.tested) * 1000) / 10 : null, met: null } : null);
  const errs = Number(sr?.metrics?.error_count ?? (sr?.findings || []).length ?? 0);
  const types = sr?.error_types || [];
  const fmt = (n) => Number(n || 0).toLocaleString();
  // Literal class names: Tailwind only ships classes it can see in source.
  const BAR = { emerald: 'bg-emerald-500', rose: 'bg-rose-500', slate: 'bg-slate-400' };
  const TXT = { emerald: 'text-emerald-700', rose: 'text-rose-700', slate: 'text-slate-600' };
  const tone = (m) => (m === true ? 'emerald' : m === false ? 'rose' : 'slate');
  const Line = ({ r, total }) => {
    const t = tone(r.met);
    const target = r.benchmark ?? (bm ? bm.value : null);
    return (
      <div className={`grid grid-cols-[minmax(11rem,1.4fr)_minmax(8rem,2fr)_4.5rem_7rem_7rem] items-center gap-x-3 py-1.5 ${total ? 'border-t border-slate-200 mt-1 pt-2' : ''}`}>
        <span className={`text-[11.5px] truncate ${total ? 'font-semibold text-slate-800' : 'text-slate-700'}`} title={r.scenario}>{total ? 'All case types' : r.scenario}</span>
        <span className="relative h-1.5 rounded-full bg-slate-100 overflow-visible">
          <span className={`absolute inset-y-0 left-0 rounded-full ${BAR[t]}`} style={{ width: `${Math.max(0, Math.min(100, Number(r.rate || 0)))}%` }} />
          {target != null && <span className="absolute -top-[3px] h-3 w-px bg-slate-500" style={{ left: `${Math.max(0, Math.min(100, Number(target)))}%` }} title={`Benchmark ${target}%`} />}
        </span>
        <span className={`text-[12px] font-semibold tabular-nums text-right ${TXT[t]}`}>{r.rate == null ? '—' : `${r.rate}%`}</span>
        <span className="text-[10.5px] tabular-nums text-slate-500 text-right" title="Timely of applicable">{fmt(r.timely)} / {fmt(r.tested)}</span>
        <span className="text-right">
          {r.met === true && <span className="px-1.5 py-px rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700">Met {target}%</span>}
          {r.met === false && <span className="px-1.5 py-px rounded-full text-[10px] font-semibold bg-rose-50 text-rose-700">Below {target}%</span>}
          {r.met == null && <span className="text-[10px] text-slate-400">No benchmark</span>}
        </span>
      </div>
    );
  };
  return (
    <div className="basis-full mt-1.5 rounded-xl bg-slate-50/70 ring-1 ring-slate-200/70 px-3.5 py-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Timeliness by measure</span>
        <span className="text-[10px] text-slate-400">Precision Scrubber · {fmt(sr?.metrics?.row_count)} rows</span>
        <span className="ml-auto text-[10px] text-slate-500">{bm ? `Benchmark ${bm.value}% · from COA monitoring` : 'No COA benchmark set for this measure'}</span>
      </div>
      {rows.length ? (
        <div className="mt-1">
          {rows.map((r) => <Line key={r.scenario} r={r} />)}
          {overall && <Line r={overall} total />}
        </div>
      ) : <p className="text-[11px] text-slate-500 mt-1">No timeliness measures were reported for this universe.</p>}
      <div className="mt-2 pt-2 border-t border-slate-200 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Data errors</span>
        {errs ? (
          <>
            <span className="px-1.5 py-px rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700">{fmt(errs)} total</span>
            {types.map((t) => <span key={t.type} className="px-1.5 py-px rounded-full text-[10px] font-semibold bg-white ring-1 ring-slate-200 text-slate-600">{t.type} {fmt(t.count)}</span>)}
          </>
        ) : <span className="px-1.5 py-px rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700">None</span>}
        <span className="ml-auto text-[10px] text-slate-400">Every error, by row, is in the error report. The recorded result above is yours.</span>
      </div>
    </div>
  );
}

const OUTCOMES = {
  'Data Integrity': [['verified', 'Verified', 'bg-emerald-50 text-emerald-700 ring-emerald-200'], ['questioned', 'Integrity questioned', 'bg-rose-50 text-rose-700 ring-rose-200']],
  default: [['compliant', 'Compliant', 'bg-emerald-50 text-emerald-700 ring-emerald-200'], ['condition', 'Condition noted', 'bg-rose-50 text-rose-700 ring-rose-200']],
};

const ANSWERS = [['yes', 'Yes', 'bg-emerald-50 text-emerald-700 ring-emerald-200'], ['no', 'No', 'bg-rose-50 text-rose-700 ring-rose-200'], ['na', 'N/A', 'bg-slate-100 text-slate-500 ring-slate-200']];

export default function ProgramAudits() {
  const [board, setBoard] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [startedProgram, setStartedProgram] = useState(null);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [err, setErr] = useState('');

  const [boardView, setBoardView] = useState('audits');
  // Which kind of audit the board shows: all three together, or one of them.
  const [classFilter, setClassFilter] = useState('all');
  const load = useCallback(() => {
    jfetch('/internal-audits').then((d) => { setBoard(d.audits); setPrograms(d.programs || []); }).catch((e) => setErr(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  // A just started audit is not on the board until it reloads; its program comes back with the start.
  const openProgram = (board || []).find((a) => a.id === openId)?.program || startedProgram;
  if (openId && openProgram === 'radv') return <RadvWorkspace id={openId} onBack={() => { setOpenId(null); load(); }} />;
  if (openId) return <AuditWorkspace id={openId} onBack={() => { setOpenId(null); load(); }} />;
  if (toolsOpen) return <ProgramToolsManager onBack={() => setToolsOpen(false)} />;

  const counts = { all: (board || []).length };
  for (const a of board || []) counts[classOf(a)] = (counts[classOf(a)] || 0) + 1;
  const shown = (board || []).filter((a) => classFilter === 'all' || classOf(a) === classFilter);

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="p-8 pt-4 max-w-6xl mx-auto w-full">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <p className="text-[11.5px] text-slate-400">CMS audits and internal audits on one board, each marked for what it is. Every audit runs the CMS way: universes in, Scrubber testing, case field work, report, and a certified purge at close. Follow-ups go from Workstream.</p>
          <span className="flex items-center gap-3">
            <button onClick={() => setToolsOpen(true)} className="text-[11.5px] font-semibold text-orange-700 hover:text-orange-900">Manage tools</button>
            <button onClick={() => setStarting(true)}
              className="px-3.5 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold">
              Start an audit
            </button>
          </span>
        </div>
        {err && <div className="mt-2 text-[11.5px] font-semibold text-rose-600">{err}</div>}
        <div className="mt-3 inline-flex rounded-xl bg-slate-100 p-1">
          {[['audits', 'Audits'], ['charting', 'Charting']].map(([k, label]) => (
            <button key={k} onClick={() => setBoardView(k)} className={`px-3.5 py-1.5 rounded-lg text-[12px] font-semibold ${boardView === k ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>{label}</button>
          ))}
        </div>
        {boardView === 'charting' && <ChartGuard><AuditCharting audits={board || []} /></ChartGuard>}
        {boardView === 'audits' && (<>
          {/* The kind of audit, as a filter row with counts: both together by default. */}
          <div className="mt-3 flex items-center gap-1.5 flex-wrap">
            {[['all', 'All audits'], ['cms', AUDIT_CLASS.cms.label], ['internal', AUDIT_CLASS.internal.label]].map(([k, label]) => (
              <button key={k} onClick={() => setClassFilter(k)} title={k === 'all' ? 'Every audit, of every kind' : AUDIT_CLASS[k].blurb}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${classFilter === k ? (k === 'all' ? 'bg-slate-900 text-white' : AUDIT_CLASS[k].pill) : 'bg-white border border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                {label} <span className={classFilter === k ? 'opacity-80' : 'text-slate-400'}>{counts[k] || 0}</span>
              </button>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {shown.map((a) => (
            <button key={a.id} onClick={() => setOpenId(a.id)}
              className="text-left bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-md hover:border-orange-200 transition-all">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <AuditClassChip audit={a} className="flex-none" />
                  <div className="text-[13.5px] font-semibold text-slate-900 uppercase truncate">{a.program}</div>
                </div>
                <span className={`flex-none px-1.5 py-px rounded-full text-[10px] font-semibold ${a.status === 'closed' ? 'bg-emerald-50 text-emerald-700' : 'bg-orange-50 text-orange-700'}`}>
                  {a.status === 'closed' ? (a.score != null ? `Closed · ${Math.round(a.score)}%` : 'Closed') : 'Open'}
                </span>
              </div>
              <div className="mt-1 text-[11.5px] text-slate-500">{a.cycle_label}</div>
              <div className="mt-2 text-[11px] text-slate-500">
                {a.program === 'radv'
                  ? <>Members {a.radv_complete}/{a.radv_members} complete · Chases open {a.radv_chases_open}{a.radv_submission_deadline ? ` · Due to CMS ${String(a.radv_submission_deadline).slice(0, 10)}` : ' · Upload the CMS notice'}</>
                  : <>Universes {a.universes_in}/{a.universes_total} · Cases decided {a.decided_count}/{a.case_count}</>}
              </div>
            </button>
          ))}
          {board && !board.length && (
            <div className="col-span-full text-[12px] text-slate-400 py-8 text-center">No audits yet. Start the ODAG audit and its universe slots and case testing questions are ready in one step.</div>
          )}
          {board && board.length > 0 && !shown.length && (
            <div className="col-span-full text-[12px] text-slate-400 py-8 text-center">No {AUDIT_CLASS[classFilter]?.label.toLowerCase() || 'audit'}s yet.</div>
          )}
          </div>
        </>)}
      </div>
      {starting && <StartAuditModal programs={programs} onDone={(g) => { setStarting(false); setStartedProgram(g.program || null); setOpenId(g.id); load(); }} onCancel={() => setStarting(false)} />}
    </div>
  );
}

// The program tool library: see every tool, download the workbook, upload
// a new version. An update is ingested on the spot and every FUTURE audit
// uses it; audits already open keep their snapshot, so the record never
// shifts underneath the auditor.
function ProgramToolsManager({ onBack }) {
  const [tools, setTools] = useState(null);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const load = useCallback(() => { jfetch('/internal-audits/tools').then((d) => setTools(d.tools)).catch((e) => setErr(e.message)); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="p-8 pt-4 max-w-4xl mx-auto w-full">
        <button onClick={onBack} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-700">← All audits</button>
        <div className="mt-1 text-[15px] font-bold text-slate-900">Program audit tools</div>
        <p className="text-[11.5px] text-slate-400 mt-0.5">Download a workbook, adjust the questions in Excel, and upload it back — the update is ingested immediately and every future audit uses it. Audits already open keep the questions they started with.</p>
        {err && <div className="mt-2 text-[11.5px] font-semibold text-rose-600">{err}</div>}
        {note && <div className="mt-2 text-[11.5px] font-semibold text-emerald-700">{note}</div>}
        <div className="mt-3 space-y-2">
          {(tools || []).map((t) => (
            <div key={t.id} className="bg-white border border-slate-200 rounded-2xl px-4 py-3 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[14rem]">
                <div className="text-[13px] font-semibold text-slate-900">{t.name}</div>
                <div className="text-[11px] text-slate-500">{t.question_count} review questions · updated {new Date(t.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
              </div>
              {t.has_workbook && (
                <a href={`${API}/api/fdr/internal-audits/tools/${t.id}/workbook`}
                  className="text-[11.5px] font-semibold text-orange-700 hover:text-orange-900 no-underline">Download the tool</a>
              )}
              {/* The package: the tool, the engagement notice, the draft
                  report and the final report, all from the same program. */}
              {['notice', 'draft', 'final'].map((kind) => (
                <button key={kind} onClick={async () => {
                  try {
                    const d = await jfetch(`/internal-audits/programs/${t.function_key}/report-template?kind=${kind}`);
                    const { exportLetterDocx } = await import('../FDR/exportLetterDocx');
                    const { fetchLogoDataUrl } = await import('../FDR/exportAsmtReportPdf');
                    const base = kind === 'notice' ? 'Engagement Notice Template'
                      : kind === 'final' ? 'Final Audit Report Template' : 'Draft Audit Report Template';
                    exportLetterDocx({
                      text: d.template_text,
                      fileBase: `${base} ${t.function_key.toUpperCase()}`,
                      logoDataUrl: await fetchLogoDataUrl(`${API}/api/deck-studio/branding/logo`),
                    });
                  } catch (e) { setErr(e.message); }
                }} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-700">
                  {kind === 'notice' ? 'Engagement notice template'
                    : kind === 'final' ? 'Final report template' : 'Draft report template'}
                </button>
              ))}
              <label className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:border-orange-400 text-[11.5px] font-semibold cursor-pointer">
                Upload a new version
                <input type="file" accept=".xlsx" className="hidden" onChange={async (e) => {
                  const f = e.target.files?.[0]; e.target.value = '';
                  if (!f) return;
                  setNote(''); setErr('');
                  try {
                    const fd = new FormData(); fd.append('file', f);
                    const d = await jfetch(`/internal-audits/tools/${t.id}/upload`, { method: 'POST', body: fd });
                    setNote(`Ingested ${d.questions} questions across ${d.sections.join(', ')}. Every future audit uses this version.`);
                    load();
                  } catch (ex) { setErr(ex.message); }
                }} />
              </label>
            </div>
          ))}
          {tools && !tools.length && <div className="text-[12px] text-slate-400 py-6 text-center">No program tools yet.</div>}
        </div>
      </div>
    </div>
  );
}

function StartAuditModal({ programs, onDone, onCancel }) {
  const year = new Date().getFullYear();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ program: programs[0]?.key || 'odag', cycle_label: `CY${year}`, period_start: '', period_end: '', enrollment_band: '', notice_date: today, audit_class: 'internal' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // RADV is always a CMS audit: it starts from the CMS letter.
  const auditClass = form.program === 'radv' ? 'cms' : form.audit_class;
  const bands = programs.find((p) => p.key === form.program)?.period_bands || [];
  const band = bands.find((x) => x.key === form.enrollment_band);
  // The protocol sets the review period from enrollment: the N weeks
  // preceding, and including, the notice date. Shown live; the dates stay
  // editable because CMS may expand the period.
  const computed = band && form.notice_date
    ? { end: form.notice_date, start: new Date(new Date(`${form.notice_date}T00:00:00Z`).getTime() - band.weeks * 7 * 86400000).toISOString().slice(0, 10) }
    : null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/45 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-[min(32rem,95vw)] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="text-[15px] font-bold text-slate-900">Start an audit</div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">The universe slots and the case testing questions come from the program tool, ready on day one. The review period follows the protocol, sized by enrollment.</div>
        </div>
        <div className="px-5 py-4 space-y-3">
          {err && <div className="text-[11.5px] font-semibold text-rose-600">{err}</div>}
          {/* Who is auditing: CMS, or the compliance team. */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Who is auditing</div>
            <div className="grid grid-cols-2 gap-2">
              {['cms', 'internal'].map((k) => (
                <button key={k} type="button" disabled={form.program === 'radv' && k !== 'cms'} onClick={() => setForm({ ...form, audit_class: k })}
                  className={`text-left rounded-xl border px-3 py-2 transition-all disabled:opacity-40 ${auditClass === k ? (k === 'cms' ? 'border-indigo-300 bg-indigo-50' : 'border-orange-300 bg-orange-50') : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                  <div className="text-[12px] font-semibold text-slate-900">{AUDIT_CLASS[k].label}</div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5">{AUDIT_CLASS[k].blurb}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Program</div>
              <select value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })}
                className="w-full text-xs border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
                {programs.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Audit cycle</div>
              <input value={form.cycle_label} onChange={(e) => setForm({ ...form, cycle_label: e.target.value })}
                className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
            </div>
            {bands.length > 0 && (<>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">MA and MAPD enrollment</div>
                <select value={form.enrollment_band} onChange={(e) => setForm({ ...form, enrollment_band: e.target.value, period_start: '', period_end: '' })}
                  className="w-full text-xs border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
                  <option value="">Set the period myself</option>
                  {bands.map((x) => <option key={x.key} value={x.key}>{x.label} · {x.weeks} weeks</option>)}
                </select>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Notice date (period ends here)</div>
                <input type="date" value={form.notice_date} onChange={(e) => setForm({ ...form, notice_date: e.target.value })}
                  className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
              </div>
            </>)}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Audit period start</div>
              <input type="date" value={form.period_start || computed?.start || ''} onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Audit period end</div>
              <input type="date" value={form.period_end || computed?.end || ''} onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
            </div>
          </div>
          {band && (
            <div className="text-[11px] text-slate-500 bg-slate-50/70 border border-slate-200 rounded-xl px-3 py-2">
              Per the protocol, {band.label.toLowerCase()} means the universes cover the {band.weeks} weeks preceding, and including, the notice date. CMS may expand the period to ensure sufficient universe size, so the dates stay editable.
            </div>
          )}
          {form.program === 'radv' && (
            <div className="text-[11px] text-slate-500 bg-slate-50/70 border border-slate-200 rounded-xl px-3 py-2">
              A RADV audit starts from the CMS letter. Name the cycle here (for example PY2024); the dates come from the notice you upload on the next screen, and the members from the CMS sample list.
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center gap-2">
          <button disabled={busy} onClick={async () => {
            setBusy(true); setErr('');
            try {
              const body = { ...form, audit_class: auditClass, period_start: form.period_start || computed?.start || '', period_end: form.period_end || computed?.end || '' };
              const d = await jfetch('/internal-audits', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
              onDone(d.engagement);
            } catch (e) { setErr(e.message); setBusy(false); }
          }} className="px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50">
            {busy ? 'Starting…' : 'Start the audit'}
          </button>
          <button onClick={onCancel} className="px-4 py-2 rounded-xl bg-white border border-slate-300 text-slate-600 text-xs font-semibold">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function AuditWorkspace({ id, onBack }) {
  const [data, setData] = useState(null);
  const [section, setSection] = useState('overview');
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [corrForm, setCorrForm] = useState(null);
  const [newMail, setNewMail] = useState(false); // the New email composer on Correspondence
  const [corrFiles, setCorrFiles] = useState([]);

  const load = useCallback(() => {
    jfetch(`/engagements/${id}`).then(setData).catch((e) => setErr(e.message));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-8 text-xs text-slate-400">{err || 'Loading…'}</div>;
  const g = data.engagement;
  const readOnly = g.status !== 'open';
  // A case set aside as not reviewed is out of every count.
  const cases = (data.case_tests || []).filter((c) => !c.not_reviewed_at);
  const decided = cases.filter((c) => c.outcome).length;
  // Universe counts judge universe tables only; a slot of kind file (a Model
  // of Care, an impact analysis) is supplemental documentation.
  const uni = (data.universe_requests || []).filter((u) => u.source_kind !== 'file');
  const uniIn = uni.filter((u) => u.status === 'received').length;
  const sections = [...new Set(data.items.map((i) => i.section))];

  // Closing runs through the Audit record panel on Results: download the
  // complete record, then purge with a typed confirmation.

  const isPda = g.program === 'pda';
  const isWebsite = g.program === 'website';
  const pda = data.pda || null;
  // Every internal audit carries its live results (audit): conditions or
  // deficiencies, responses, dispositions, charts, and what blocks the final.
  const audit = data.audit || null;
  const TABS = [
    ['overview', 'Overview', null],
    ['fieldwork', 'Fieldwork', `${decided}/${cases.length}`],
    ...(audit ? [['responses', 'Responses', audit?.analysis?.responses?.required ? `${audit.analysis.responses.dispositioned}/${audit.analysis.responses.required}` : null]] : []),
    ['correspondence', 'Correspondence', (data.correspondence || []).length || null],
    ['results', 'Results', null],
  ];

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="p-8 pt-4 max-w-6xl mx-auto w-full">
        <button onClick={onBack} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-700">← All audits</button>
        <div className="mt-1 flex items-center gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <AuditClassChip audit={g} />
              <div className="text-[17px] font-bold text-slate-900 uppercase">{g.program} · {g.cycle_label}</div>
            </div>
            <div className="text-[11.5px] text-slate-500">
              {AUDIT_CLASS[classOf(g)].label}{g.period_start ? ` · Audit period ${String(g.period_start).slice(0, 10)} to ${String(g.period_end || '').slice(0, 10)}` : ''} · follow-ups go from Workstream
            </div>
          </div>
          <span className="ml-auto flex items-center gap-2">
            {g.sent_at && (
              <span className="text-[10.5px] text-slate-400" title={g.sent_to || ''}>
                Notice sent {new Date(g.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                {/* The sent notice stays open to the auditor: view it as it went out, edit the text, save. */}
                <button onClick={() => setNoticeOpen('edit')}
                  title={readOnly ? 'View the notice as it went out' : 'View the notice as it went out, edit the text and save it on the audit'}
                  className="ml-1.5 font-semibold text-orange-600 hover:text-orange-700">{readOnly ? 'View' : 'View & edit'}</button>
                {!readOnly && g.status !== 'closed' && (
                  <button onClick={() => setNoticeOpen('resend')}
                    title="Send the notice again, formatted and with the Word file attached. The first send stays on the record; the resend is logged as its own entry."
                    className="ml-1.5 font-semibold text-orange-600 hover:text-orange-700">Resend</button>
                )}
              </span>
            )}
            <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${g.status === 'closed' ? 'bg-emerald-50 text-emerald-700' : 'bg-orange-50 text-orange-700'}`}>
              {g.status === 'closed' ? 'Closed' : 'Open'}
            </span>
            {!readOnly && !g.sent_at && (
              <button onClick={() => setNoticeOpen(true)}
                title="Review the engagement notice, then send it to the internal recipients or record it as sent from your own mailbox — nothing goes out until you choose"
                className="px-3.5 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold">
                Audit notice — review &amp; send
              </button>
            )}
            {!readOnly && (
              <button onClick={() => { setSection('results'); setPurgeOpen(true); }}
                title="Download the complete audit record, then purge the working material and close with a destruction certificate"
                className="px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold">
                Close &amp; score
              </button>
            )}
          </span>
        </div>
        {note && <div className="mt-1.5 text-[11.5px] font-semibold text-emerald-700">{note}</div>}
        {err && <div className="mt-1.5 text-[11.5px] font-semibold text-rose-600">{err}</div>}

        <div className="mt-4 border-b border-slate-200 flex items-center gap-1 flex-wrap">
          {TABS.map(([k, label, badge]) => (
            <button key={k} onClick={() => setSection(k)}
              className={`px-3.5 py-2 text-[12.5px] font-semibold border-b-2 -mb-px transition-colors ${section === k ? 'border-orange-500 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
              {label}{badge != null && <span className="ml-1.5 px-1.5 py-px rounded-full bg-slate-100 text-[10px] text-slate-500 tabular-nums">{badge}</span>}
            </button>
          ))}
        </div>

        {section === 'overview' && (
          <div className="mt-4">
            <div className="bg-slate-50/70 border border-slate-200 rounded-2xl px-4 py-3 text-[12.5px] text-slate-700">
              <span className="font-semibold text-slate-500 mr-1.5">Next:</span>
              {g.status === 'closed' ? 'This audit is closed — the results and the destruction certificate are under Results.'
                : isPda && !g.sent_at ? 'Open "Audit notice — review & send" (top right) and send the notice to the operational leadership. Then complete the audit setup below: the goal and the methodology print in the report. Nothing is requested from the business.'
                : isPda && !cases.length ? 'Draw the sample from the published online directory and load it on Fieldwork (upload the listings or add them one by one). Every provider type under review should appear.'
                : isPda && decided < cases.length ? 'Work the calls on Fieldwork: log each attempt, answer the questions in order; the result is derived for you. Then issue the draft report from Results.'
                : isPda && !g.report_sent_at ? 'Fieldwork is complete. Write the qualitative analysis on Results and issue the draft report: each recipient gets a personal response link.'
                : isPda && !g.report_final_at ? 'The department is responding. Disposition every response on the Responses tab, record corrections on Fieldwork, then issue the final report.'
                : isPda ? 'The final report is issued. Close & score when corrections are validated; the purge and certificate happen at close.'
                : isWebsite && !g.sent_at ? 'Open "Audit notice — review & send" (top right) and send the notice to Marketing, Digital, Member Services and Pharmacy. Nothing is requested from the business beyond the plan list, the site addresses and the Material IDs.'
                : isWebsite && !cases.length ? 'Load the rows on Fieldwork: the plans in each county (Plan Documents), the pages of the Medicare section (Website Content), each site or app (General Requirements), and each online tool (Directories and Formulary). Upload the listings or add them one by one.'
                : isWebsite && decided < cases.length ? 'Work the rows on Fieldwork: open the page on the date you review it, answer each question Yes, No or N/A, and record the URL and what the page showed in the comments. Then issue the draft report from Results.'
                : isWebsite && !g.report_sent_at ? 'Fieldwork is complete. Issue the draft report from Results: each recipient gets a personal response link.'
                : isWebsite && !g.report_final_at ? 'The areas are responding. Disposition every response on the Responses tab, then issue the final report.'
                : isWebsite ? 'The final report is issued. Close & score when the corrections are validated on the live site.'
                : !g.sent_at ? 'Open "Audit notice — review & send" (top right). Review the notice, then send it to the operational leadership or record it as sent from your own mailbox — nothing goes out until you choose.'
                : !g.report_sent_at && uniIn === uni.length && decided === cases.length && cases.length ? 'Fieldwork is complete. Issue the draft report from Results: each recipient gets a personal response link, the compliance officers are copied.'
                : g.report_sent_at && !g.report_final_at ? 'The areas under review are responding. Disposition every response on the Responses tab, record corrections on Fieldwork, then issue the final report.'
                : g.report_final_at ? 'The final report is issued. Close & score when corrective actions are validated; the purge and certificate happen at close.'
                : uniIn < uni.length ? 'Universes are arriving. File each one into its slot, run it through the Scrubber, and mark data problems as you find them.'
                : decided < cases.length || !cases.length ? 'Universes are in. Select the cases per the guideline on each Fieldwork tab and work the questions, case by case.'
                : 'Case testing is complete. Review the outcomes, then Close & score — the purge and certificate happen at close.'}
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
              {[// The website audit counts findings, not rows (operator order
                // 2026-11-02): the cards say rows reviewed, findings, and
                // requirements met; a protocol program keeps its universes
                // and cases.
                isWebsite
                  ? ['Findings', audit?.analysis?.totals?.grouped ? audit.analysis.totals.grouped.total : '—', audit?.analysis?.totals?.grouped ? `${audit.analysis.totals.grouped.car + (audit.analysis.totals.grouped.ids || 0)} CAR · ${audit.analysis.totals.grouped.observation} observation${audit.analysis.totals.grouped.observation === 1 ? '' : 's'}` : 'conditions and observations', () => setSection('results')]
                  : ['Universes', uni.length ? `${uniIn}/${uni.length}` : '—', 'received', () => setSection('fieldwork')],
                isWebsite
                  ? ['Rows reviewed', cases.length ? `${decided}/${cases.length}` : '—', 'plans, pages, sites and tools', () => setSection('fieldwork')]
                  : ['Cases decided', cases.length ? `${decided}/${cases.length}` : '—', 'sampled cases', () => setSection('fieldwork')],
                ['Correspondence', (data.correspondence || []).length || '—', 'on the record', () => setSection('correspondence')],
                ['Status', g.status === 'closed' ? (g.score != null ? `${Math.round(g.score)}%` : 'Closed') : (isWebsite && audit?.analysis?.totals?.requirements?.tested ? `${audit.analysis.totals.requirements.ratePct ?? 0}%` : 'In progress'),
                  g.status === 'closed' ? (isWebsite ? 'requirements met' : 'pass rate') : (isWebsite && audit?.analysis?.totals?.requirements?.tested ? `${audit.analysis.totals.requirements.met} of ${audit.analysis.totals.requirements.tested} requirements met` : ''), () => setSection('results')]].map(([t, big, sub, go]) => (
                <button key={t} onClick={go} className="text-left bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-md hover:border-orange-200 transition-all">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t}</div>
                  <div className="mt-1 text-[22px] font-bold leading-none tabular-nums text-slate-900">{big}</div>
                  <div className="mt-1 text-[11px] text-slate-500">{sub}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {section === 'overview' && isPda && (
          <PdaSetupCard g={g} settings={pda?.settings} readOnly={readOnly} onChanged={load} setErr={setErr} />
        )}

        {section === 'fieldwork' && (
          <FieldworkTab data={data} g={g} readOnly={readOnly} sections={sections} onChanged={load} setErr={setErr} />
        )}

        {section === 'responses' && audit && (() => { const f = findingsOf(audit, g.id); return (
          <ResponsesTab g={g} findings={f.findings} overturned={f.overturned} counts={f.counts} noun={f.noun} links={audit.response_links} readOnly={readOnly} onChanged={load} setErr={setErr} />
        ); })()}

        {noticeOpen && (
          <NoticeModal g={g} mode={noticeOpen === 'resend' ? 'resend' : noticeOpen === 'edit' ? 'edit' : 'send'} readOnly={readOnly}
            onDone={() => { const wasResend = noticeOpen === 'resend'; setNoticeOpen(false); setNote(wasResend ? 'Notice resent and on the record as its own entry.' : (isPda ? 'Notice on the record. Nothing is requested from the business; complete the audit setup and draw the sample.' : isWebsite ? 'Notice on the record. The plan list, the site addresses and the Material IDs are due in 5 business days; load the rows on Fieldwork.' : 'Notice on the record — universe submissions are due 15 business days out.')); load(); }}
            onCancel={() => { setNoticeOpen(false); load(); }} />
        )}

        {section === 'correspondence' && (
          <div className="mt-4 bg-white border border-slate-200 rounded-2xl px-4 py-3">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Correspondence · {(data.correspondence || []).length}</span>
              <button onClick={() => { setNewMail(!newMail); setCorrForm(null); }}
                className="ml-auto text-[11px] font-semibold text-orange-700 hover:text-orange-900">
                {newMail ? 'Cancel' : '+ New email'}
              </button>
              <button onClick={() => { setCorrForm(corrForm ? null : { kind: 'inbound', subject: '', body: '' }); setNewMail(false); }}
                className="text-[11px] font-semibold text-orange-700 hover:text-orange-900">
                {corrForm ? 'Cancel' : '+ Log correspondence'}
              </button>
            </div>
            <div className="mb-2 text-[11px] text-slate-500">Every email this audit sent, from any door, and every reply that came back to its address, in one place. A failed send stays here marked failed; one opened in your mail program stays marked until your copy arrives.</div>
            {newMail && (
              <EmailReplyComposer mode="new" to={[]} subject={`${g.cycle_label || 'Audit'} · `} prior={null}
                onSend={async ({ to, cc, subject, body, files }) => {
                  const fd = new FormData();
                  fd.append('to', to.join(', ')); fd.append('cc', cc.join(', ')); fd.append('subject', subject); fd.append('body', body);
                  for (const f of files) fd.append('files', f);
                  await jfetch(`/engagements/${g.id}/correspondence/send`, { method: 'POST', body: fd });
                  load();
                }}
                onClose={() => setNewMail(false)} />
            )}
            {corrForm && (
              <div className="mb-2.5 bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
                <div className="flex gap-2">
                  <select value={corrForm.kind} onChange={(e) => setCorrForm({ ...corrForm, kind: e.target.value })}
                    className="text-[11px] border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
                    <option value="inbound">Received (paste an email or file it)</option>
                    <option value="outbound">Sent (record an email you sent)</option>
                    <option value="note">Internal note</option>
                  </select>
                  <input value={corrForm.subject} onChange={(e) => setCorrForm({ ...corrForm, subject: e.target.value })} placeholder="Subject"
                    className="flex-1 text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
                </div>
                <textarea value={corrForm.body} onChange={(e) => setCorrForm({ ...corrForm, body: e.target.value })} rows={3}
                  placeholder="Paste the email or write the note — it becomes part of the audit record"
                  className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:border-orange-400 text-[11px] font-semibold cursor-pointer">
                    Attach files{corrFiles.length ? ` · ${corrFiles.length}` : ''}
                    <input type="file" multiple className="hidden" onChange={(e) => { setCorrFiles([...corrFiles, ...Array.from(e.target.files || [])]); e.target.value = ''; }} />
                  </label>
                  {corrFiles.map((f, i) => (
                    <span key={i} className="text-[10.5px] text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{f.name}
                      <button onClick={() => setCorrFiles(corrFiles.filter((_, j) => j !== i))} className="ml-1 text-slate-400 hover:text-rose-600">✕</button>
                    </span>
                  ))}
                </div>
                <button onClick={async () => {
                  try {
                    const fd = new FormData();
                    fd.append('kind', corrForm.kind); fd.append('subject', corrForm.subject); fd.append('body', corrForm.body);
                    for (const f of corrFiles) fd.append('files', f);
                    await jfetch(`/engagements/${g.id}/correspondence`, { method: 'POST', body: fd });
                    setCorrForm(null); setCorrFiles([]); load();
                  } catch (e) { setErr(e.message); }
                }} disabled={!corrForm.body.trim() && !corrFiles.length}
                  className="px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-semibold disabled:opacity-50">Save to the record</button>
              </div>
            )}
            {(data.correspondence || []).map((c) => (
              <details key={c.id} className="py-1 border-t border-slate-50 first:border-t-0">
                <summary className="cursor-pointer text-[11.5px] text-slate-600 flex items-center gap-2">
                  <span className={`px-1.5 py-px rounded-full text-[9.5px] font-bold uppercase tracking-wide ${
                    c.kind === 'outbound' || c.kind === 'letter_sent' || c.kind === 'report_sent' || c.kind === 'report_final' ? 'bg-orange-50 text-orange-700'
                    : c.kind === 'inbound' ? 'bg-sky-50 text-sky-700' : c.kind === 'reminder_sent' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                    {c.kind.replace('_', ' ')}
                  </span>
                  {c.status === 'failed' && <span className="px-1.5 py-px rounded-full text-[9.5px] font-bold uppercase tracking-wide bg-rose-50 text-rose-700">not sent</span>}
                  {c.status === 'opened_in_mail_program' && <span className="px-1.5 py-px rounded-full text-[9.5px] font-bold uppercase tracking-wide bg-amber-50 text-amber-800">in your mail program</span>}
                  <span className="truncate">{c.subject || '—'}</span>
                  <span className="ml-auto text-[10.5px] text-slate-400 flex-none">
                    {(c.to_emails || c.to_email) ? `${c.to_emails || c.to_email} · ` : ''}{new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{c.by_name ? ` · ${c.by_name}` : ''}
                  </span>
                </summary>
                {(c.email_subject || c.can_reply) && <SentEmailView c={c} engagementId={g.id} onSent={load} />}
                {c.body && <div className="mt-1.5 mb-1 whitespace-pre-wrap text-[11px] text-slate-600 bg-slate-50 rounded-lg px-3 py-2 max-h-56 overflow-y-auto">{c.body}</div>}
                {(data.documents || []).filter((d) => d.correspondence_id === c.id).map((d) => (
                  <div key={d.id} className="mt-1 flex items-center gap-2 text-[11px] text-slate-600 bg-slate-50 rounded-lg px-2.5 py-1.5">
                    <span>📎</span><span className="truncate flex-1">{d.file_name}</span>
                    {d.purged
                      ? <span className="text-[10px] font-semibold text-slate-400">Purged</span>
                      : <a href={`${API}/api/fdr/documents/${d.id}/download`} className="text-[10.5px] font-semibold text-orange-700 hover:text-orange-900 no-underline">Download</a>}
                  </div>
                ))}
              </details>
            ))}
          </div>
        )}

        {section === 'results' && (
          <div className="mt-4 space-y-3">
            {audit && <PdaCharts charts={audit.charts} />}
            {isPda && !g.report_final_at && (
              <PdaQualitativeForm g={g} analysis={pda?.analysis} qualitative={pda?.qualitative} readOnly={readOnly} onChanged={load} setErr={setErr} />
            )}
            {!isPda && audit && (
              <ConditionsPanel g={g} analysis={audit.analysis} cases={cases} website={isWebsite} readOnly={readOnly || !!g.report_final_at} onChanged={load} setErr={setErr} />
            )}
            <InternalReportPanel g={g} onChanged={load} setErr={setErr} pda={audit} />
            <AuditRecordPanel g={g} jfetch={jfetch} onChanged={load} readOnly={readOnly} purgeOpen={purgeOpen} setPurgeOpen={setPurgeOpen} />
            {g.status === 'closed' && (
              <div className="bg-emerald-50/60 border border-emerald-200 rounded-2xl px-4 py-3 text-[12px] text-emerald-900 flex flex-wrap gap-x-6 gap-y-1">
                <span><b>{g.score != null ? `${Math.round(g.score)}%` : '—'}</b> {isWebsite ? 'of requirements met' : 'of decided cases passed'}</span>
                <span><b>{g.met_count}</b> {isWebsite ? 'requirements met' : 'verified or compliant'}</span>
                <span><b>{g.not_met_count}</b> {isWebsite ? 'requirements with a finding' : 'questioned or condition'}</span>
                {g.phi_purged_at && <span className="text-emerald-700">Working material destroyed and certified {new Date(g.phi_purged_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
              </div>
            )}
            {g.status === 'closed' && g.phi_purge_certificate && (
              <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Destruction certificate</div>
                <div className="text-[12px] text-slate-700">{g.phi_purge_certificate.phi_working_material}</div>
                {(g.phi_purge_certificate.purged_artifacts || []).map((a, i) => (
                  <div key={i} className="mt-1 flex items-center gap-2 text-[11.5px] text-slate-600 bg-slate-50 rounded-lg px-2.5 py-1.5">
                    <span className="truncate flex-1">{a.file_name}</span>
                    <span className="tabular-nums text-slate-400">{Math.round((a.bytes || 0) / 1024)} KB erased</span>
                    <span className="font-mono text-[10px] text-slate-400">{(a.sha256 || '').slice(0, 16)}…</span>
                  </div>
                ))}
                <div className="mt-1.5 text-[10.5px] text-slate-400">Universe files, sample files, and case identifiers are gone. Outcomes, comments, and hashes remain as the record.</div>
              </div>
            )}
            {g.status !== 'closed' && (
              <div className="bg-slate-50/70 border border-slate-200 rounded-2xl px-4 py-3 text-[12px] text-slate-600">
                <span className="font-semibold text-slate-700">Close &amp; score</span> computes the case results and performs the purge: every universe and sample file is erased, every case identifier is cleared, the erasure is verified, and the destruction certificate lists what was destroyed. Outcomes and comments survive; member data does not.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// The engagement notice, reviewed by a human before anything moves: send it
// to the internal recipients through the app, or record it as sent from
// your own mailbox. Word and PDF download under the client letterhead.
//
// Three ways in (operator order 2026-10-15): before the send, "review & send";
// after the send, "view & edit" (the notice stays editable and the edits are
// saved); and "resend". In every one the notice shows EXACTLY as it goes out:
// the server renders the on-screen copy with the same renderer, theme, kicker
// and attachment line as the email (POST .../notice/preview), and an "Edit
// text" toggle opens the plain text beside it. Save writes the text
// (PUT .../notice) whether the notice is a draft or already sent.
function NoticeModal({ g, onDone, onCancel, mode = 'send', readOnly = false }) {
  const resend = mode === 'resend';
  const editing = mode === 'edit';
  const [letter, setLetter] = useState('');
  const [stored, setStored] = useState(null);     // the text on record (null until loaded)
  const [view, setView] = useState('preview');     // 'preview' | 'edit'
  const [preview, setPreview] = useState(null);    // { html, subject, attachment, from_name }
  const [previewing, setPreviewing] = useState(false);
  // Recipients are REGISTERED ORG USERS, one or more, picked — never typed
  // (operator order 2026-08-28). The sender and the organization's
  // compliance officers are copied automatically.
  const [toIds, setToIds] = useState(new Set());
  const [ccIds, setCcIds] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState('');
  useEffect(() => {
    jfetch(`/internal-audits/${g.id}/notice`)
      .then((d) => { setLetter(d.letter_text || ''); setStored(d.stored ? (d.letter_text || '') : ''); })
      .catch((e) => setErr(e.message));
  }, [g.id]);
  // The rendered letter follows the text, a beat behind the typing.
  useEffect(() => {
    if (stored === null) return undefined;
    setPreviewing(true);
    const t = setTimeout(() => {
      jfetch(`/internal-audits/${g.id}/notice/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ letter_text: letter }),
      }).then(setPreview).catch((e) => setErr(e.message)).finally(() => setPreviewing(false));
    }, 350);
    return () => clearTimeout(t);
  }, [g.id, letter, stored]);
  const dirty = stored !== null && letter.trim() !== stored.trim();
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true); setErr(''); setSaved('');
    try {
      const d = await jfetch(`/internal-audits/${g.id}/notice`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ letter_text: letter }),
      });
      setStored(d.letter_text || ''); setLetter(d.letter_text || '');
      setSaved(`Saved ${new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}.`);
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };
  const send = async (manual) => {
    setBusy(true); setErr('');
    try {
      await jfetch(`/internal-audits/${g.id}/notice/${resend ? 'resend' : 'send'}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_user_ids: [...toIds], cc_user_ids: [...ccIds], letter_text: letter, manual }),
      });
      onDone();
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  const close = () => {
    if (dirty && !window.confirm('You have unsaved changes to the notice. Close without saving?')) return;
    onCancel();
  };
  const fileBase = `Audit Engagement Notice - ${String(g.program || '').toUpperCase()} ${g.cycle_label}`;
  const sentOn = g.sent_at ? new Date(g.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  const canEdit = !readOnly;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/45 flex items-center justify-center p-4" onClick={close}>
      <div className="bg-white rounded-2xl shadow-2xl w-[min(60rem,96vw)] max-h-[94vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
          <div className="min-w-0">
            <div className="text-[15px] font-bold text-slate-900">
              {resend ? 'Resend the audit engagement notice' : editing ? 'Audit engagement notice' : 'Audit engagement notice'}
            </div>
            <div className="text-[11.5px] text-slate-500 mt-0.5">
              {resend
                ? 'The notice goes out again, formatted under the letterhead with the Word file attached. Correct the text if needed. The first send stays on the record; this one is logged as its own entry.'
                : editing
                  ? (canEdit
                    ? 'This is the notice on record, shown as it went out. Edit the text and save; the saved text is what a resend carries. The copy that was sent stays on Correspondence untouched.'
                    : 'This audit is closed. The notice is part of the record and shows as it went out.')
                  : `Review every word. Send it through the app or record it as sent from your own mailbox — nothing goes out until you choose.${g.program === 'pda' ? ' Nothing is requested from the business.' : g.program === 'website' ? ' The plan list, the site addresses and the Material IDs come due 5 business days after the notice.' : ' Universe submissions come due 15 business days after the notice.'}`}
            </div>
          </div>
          <span className={`ml-auto shrink-0 px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${g.sent_at ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}
            title={g.sent_at ? (g.sent_to || '') : 'Not sent yet'}>
            {g.sent_at ? `Final · sent ${sentOn}` : 'Draft · not sent'}
          </span>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
          {err && <div className="mb-2 text-[11.5px] font-semibold text-rose-600">{err}</div>}
          {!editing && (
            <div className="space-y-2 mb-3">
              <OrgCcPicker label="To: the operational leadership under review (registered users, one or more)" ccIds={toIds} setCcIds={setToIds} />
              <OrgCcPicker label="Also copy (registered users)" ccIds={ccIds} setCcIds={setCcIds} />
              <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-slate-600">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Copied automatically</span>
                <span className="px-1.5 py-px rounded bg-slate-100">You (the sender)</span>
                <span className="px-1.5 py-px rounded bg-slate-100">The compliance officer(s)</span>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              {view === 'preview' ? 'The notice, as it goes out' : 'The notice, plain text'}
            </span>
            <span className="ml-auto inline-flex rounded-lg border border-slate-200 overflow-hidden text-[11px] font-semibold">
              <button onClick={() => setView('preview')} className={`px-2.5 py-1 ${view === 'preview' ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:text-slate-700'}`}>Letter</button>
              <button onClick={() => setView('edit')} className={`px-2.5 py-1 ${view === 'edit' ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:text-slate-700'}`}>{canEdit ? 'Edit text' : 'Plain text'}</button>
            </span>
          </div>
          {view === 'preview' ? (
            <div className="border border-slate-200 rounded-xl overflow-hidden bg-[#f4f5f7]">
              {preview && (
                <div className="px-4 py-2 bg-white border-b border-slate-200 text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-0.5">
                  <span><span className="font-semibold text-slate-400">From</span> {preview.from_name}</span>
                  <span><span className="font-semibold text-slate-400">Subject</span> {preview.subject}</span>
                  <span><span className="font-semibold text-slate-400">Attachment</span> {preview.attachment}</span>
                  {previewing && <span className="text-slate-400">Updating…</span>}
                </div>
              )}
              {preview
                ? <iframe title="The notice as it goes out" sandbox="" srcDoc={preview.html} className="w-full bg-[#f4f5f7]" style={{ height: 'min(62vh, 760px)', border: 0 }} />
                : <div className="p-6 text-[11.5px] text-slate-400">{stored === null ? 'Loading the notice…' : 'Rendering…'}</div>}
            </div>
          ) : (
            <textarea value={letter} onChange={(e) => setLetter(e.target.value)} rows={22} readOnly={!canEdit}
              className={`w-full text-[11.5px] font-mono border border-slate-300 rounded-xl px-3 py-2.5 leading-relaxed ${canEdit ? '' : 'bg-slate-50 text-slate-600'}`} />
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center gap-2 flex-wrap">
          {canEdit && (
            <button onClick={save} disabled={busy || saving || !dirty || letter.trim().length < 200}
              title={dirty ? 'Save the text of the notice on the audit' : 'Nothing changed since the last save'}
              className={`px-4 py-2 rounded-xl text-xs font-semibold disabled:opacity-50 ${editing ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-white border border-slate-300 hover:border-slate-400 text-slate-700'}`}>
              {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </button>
          )}
          {!editing && (
            <button onClick={() => send(false)} disabled={busy || !toIds.size || letter.trim().length < 200}
              className="px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50">
              {busy ? 'Sending…' : (resend ? 'Resend the notice' : 'Send the notice')}
            </button>
          )}
          {!resend && !editing && (
            <button onClick={() => send(true)} disabled={busy || !toIds.size || letter.trim().length < 200}
              title="You sent (or will send) the notice from your own mailbox — this records it and moves the audit forward, no email from the platform"
              className="px-4 py-2 rounded-xl bg-white border border-slate-300 hover:border-slate-400 text-slate-600 text-xs font-semibold disabled:opacity-50">
              Mark as sent outside the platform
            </button>
          )}
          <button onClick={close} className="px-4 py-2 rounded-xl bg-white border border-slate-300 text-slate-600 text-xs font-semibold">{editing && !dirty ? 'Close' : 'Cancel'}</button>
          {saved && !dirty && <span className="text-[11px] font-semibold text-emerald-700">{saved}</span>}
          {dirty && <span className="text-[11px] font-semibold text-amber-700">Unsaved changes</span>}
          <span className="ml-auto flex items-center gap-3">
            <button onClick={() => downloadLetterDocx(g.id, letter, fileBase).catch((e) => setErr(e.message))} disabled={letter.trim().length < 200}
              className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-40">Download Word</button>
            <button onClick={async () => {
              const { exportAsmtReportPdf, fetchLogoDataUrl } = await import('../FDR/exportAsmtReportPdf');
              exportAsmtReportPdf({ text: letter, fileBase, logoDataUrl: await fetchLogoDataUrl(`${API}/api/deck-studio/branding/logo`) });
            }} disabled={letter.trim().length < 200}
              className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-40">Download PDF</button>
          </span>
        </div>
      </div>
    </div>
  );
}

// The report cycle, called from the fieldwork: every failed case with its
// comments becomes a condition in the draft, classified in CMS language.
// The auditor edits, records the draft as issued for management response,
// then freezes the final by hash. Word and PDF under the client logo.
// The auditee and everyone copied are REGISTERED ORG USERS, searched and
// multi-selected — an internal report never goes to a typed outside
// address. One picker serves both rows.
function OrgCcPicker({ label, ccIds, setCcIds }) {
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  useEffect(() => { jfetch('/assessment-team').then((d) => setTeam(d.users || [])).catch(() => {}); }, []);
  const matches = team.filter((u) => !q.trim() || `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div>
      <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-slate-600">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
        {team.filter((u) => ccIds.has(u.id)).map((u) => (
          <span key={u.id} className="px-1.5 py-px rounded bg-orange-50 text-orange-800">{u.name || u.email}
            <button onClick={() => setCcIds((p) => { const n = new Set(p); n.delete(u.id); return n; })} className="ml-1 text-orange-400 hover:text-orange-700">✕</button>
          </span>
        ))}
        <button onClick={() => { setOpen((v) => !v); setQ(''); }} className="font-semibold text-orange-700 hover:text-orange-900">{open ? 'Done' : '+ Pick users'}</button>
      </div>
      {open && (
        <div className="mt-1.5 border border-slate-200 rounded-lg bg-white">
          <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus placeholder="Search by name or email — check everyone to copy"
            className="w-full text-xs border-b border-slate-100 rounded-t-lg px-2.5 py-1.5 focus:outline-none" />
          <div className="max-h-32 overflow-y-auto p-1.5 space-y-0.5">
            {matches.map((u) => (
              <label key={u.id} className="flex items-center gap-2 text-[11.5px] text-slate-600 hover:bg-orange-50 rounded px-1.5 py-1 cursor-pointer">
                <input type="checkbox" checked={ccIds.has(u.id)} className="accent-orange-600"
                  onChange={() => setCcIds((p) => { const n = new Set(p); if (n.has(u.id)) n.delete(u.id); else n.add(u.id); return n; })} />
                <span className="truncate">{u.name || u.email}</span>
                <span className="ml-auto text-slate-400 truncate">{u.email}</span>
              </label>
            ))}
            {!matches.length && <div className="text-[11px] text-slate-400 px-1.5 py-1">No one matches.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/** PDA quantitative charts: the computed factor rates drawn as bars against
 *  the goal line, plus the CMS county deficiency view. Same payload the PDF
 *  exhibit draws, so screen and document can never disagree. */
function PdaAnalysisCharts({ analysis }) {
  if (!analysis || !Array.isArray(analysis.factors) || !analysis.factors.length) return null;
  const goal = analysis.goalPct;
  const Bar = ({ label, pct, meets, value, deficiencyBar = false }) => (
    <div className="flex items-center gap-2">
      <span className="w-32 shrink-0 text-[10.5px] text-slate-600 truncate" title={label}>{label}</span>
      <span className="relative flex-1 h-3 rounded-full bg-slate-100 overflow-visible">
        {pct != null && (
          <span className={`absolute inset-y-0 left-0 rounded-full ${meets ? 'bg-emerald-500' : 'bg-rose-500'}`} style={{ width: `${Math.max(2, Math.min(100, pct))}%` }} />
        )}
        {!deficiencyBar && <span className="absolute -inset-y-0.5 w-px bg-slate-800" style={{ left: `${goal}%` }} title={`Performance goal ${goal}%`} />}
      </span>
      <span className={`w-28 shrink-0 text-right text-[10.5px] font-semibold tabular-nums ${pct == null ? 'text-slate-400' : meets ? 'text-emerald-700' : 'text-rose-700'}`}>{value}</span>
    </div>
  );
  return (
    <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 space-y-3.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Quantitative results, measured from the fieldwork · goal {goal}% (tick on each bar)</p>
      {analysis.factors.map((f) => (
        <div key={f.area} className="space-y-1.5">
          <p className="text-[11px] font-semibold text-slate-700">{f.title}</p>
          {!f.decided ? (
            <p className="text-[10.5px] text-slate-400">No concluded listings yet{f.total ? ` (${f.total} in review)` : ''}.</p>
          ) : (
            <>
              <Bar label="Overall" pct={f.ratePct} meets={f.ratePct >= goal} value={`${f.ratePct}% (${f.accurate}/${f.decided})`} />
              {f.groups.map((grp) => (
                <Bar key={grp.name} label={grp.name} pct={grp.ratePct} meets={grp.ratePct >= goal} value={`${grp.ratePct}% (${grp.num}/${grp.den})`} />
              ))}
              {f.counties.length > 0 && (
                <>
                  <p className="text-[10px] text-slate-400 pt-0.5">CMS county view, location deficiency rate (lower is better)</p>
                  {f.counties.map((cv) => (
                    <Bar key={cv.name} label={cv.name} pct={cv.ratePct} meets={cv.deficient === 0} value={`${cv.ratePct}% deficient (${cv.deficient}/${cv.total})`} deficiencyBar />
                  ))}
                </>
              )}
              {f.drivers.length > 0 && (
                <>
                  <p className="text-[10px] text-slate-400 pt-0.5">What failed, by count — the measured underlying reasons</p>
                  {f.drivers.map((d) => (
                    <Bar key={d.question} label={d.question} pct={(d.count / Math.max(1, f.drivers[0].count)) * 100} meets={false}
                      value={`${d.count} listing${d.count === 1 ? '' : 's'}`} deficiencyBar />
                  ))}
                </>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The conditions of a protocol program audit, the way CMS writes them: one
 * requirement not met with its sample cases. The auditor classifies each
 * one and writes the cause, the effect and the corrective action here; the
 * draft report prints them and cannot issue until cause and effect are
 * written on every standing condition.
 */
// The name a website row goes by: the plan, the page, the site or the tool,
// else the first question that carries a name, else its number.
const rowName = (c) => c.case_ref || (c.question_refs && typeof c.question_refs === 'object' ? (Object.values(c.question_refs).find((x) => x && x.name) || {}).name : null) || `Row ${c.case_no}`;

// An observation written by hand (website audit, operator order 2026-11-02):
// the element, what was seen, and the rows it points at. It prints under
// Observations for the element and needs no corrective action plan. Added
// only before the draft issues, so the draft and the record agree.
function AddObservation({ g, areas, cases, onChanged, setErr }) {
  const [open, setOpen] = useState(false);
  const [area, setArea] = useState(areas[0] || '');
  const [statement, setStatement] = useState('');
  const [rows, setRows] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const rowsOf = cases.filter((c) => c.section === area && !c.not_reviewed_at);
  const toggle = (id) => setRows((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await jfetch(`/internal-audits/${g.id}/observations`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ area, statement, case_ids: [...rows] }) });
      setStatement(''); setRows(new Set()); setOpen(false); onChanged();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="text-[11px] font-semibold text-orange-700 hover:text-orange-900" title="Write an observation on an element, apart from the rows' answers: it prints under Observations and needs no corrective action plan">
        + Add an observation
      </button>
    );
  }
  return (
    <div className="mt-2 rounded-xl bg-sky-50/60 ring-1 ring-sky-200 px-3.5 py-2.5 space-y-2">
      <div className="text-[10px] font-bold uppercase tracking-wider text-sky-800">New observation</div>
      <div className="grid md:grid-cols-[14rem_1fr] gap-2">
        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Audit element
          <select value={area} onChange={(e) => { setArea(e.target.value); setRows(new Set()); }} className="mt-0.5 w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
            {areas.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">What was seen, and where
          <textarea value={statement} onChange={(e) => setStatement(e.target.value)} rows={2} placeholder="For example: the Spanish site mirrors the English one, but its footer links open English pages" className="mt-0.5 w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white" />
        </label>
      </div>
      {rowsOf.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Rows it points at (optional)</div>
          <div className="mt-1 max-h-32 overflow-y-auto flex flex-wrap gap-1.5">
            {rowsOf.map((c) => (
              <label key={c.id} className={`text-[11px] px-2 py-0.5 rounded-lg ring-1 cursor-pointer ${rows.has(c.id) ? 'bg-sky-100 text-sky-900 ring-sky-300' : 'bg-white text-slate-600 ring-slate-200'}`}>
                <input type="checkbox" className="mr-1 align-middle" checked={rows.has(c.id)} onChange={() => toggle(c.id)} />{rowName(c)}
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center gap-2">
        <button type="button" disabled={busy || statement.trim().length < 10} onClick={submit} className="px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-sky-700 text-white disabled:opacity-50">Add the observation</button>
        <button type="button" onClick={() => setOpen(false)} className="text-[11px] font-semibold text-slate-500 hover:text-slate-700">Cancel</button>
        <span className="text-[10.5px] text-slate-500">Prints under Observations for the element; the areas acknowledge it on the response link. No corrective action plan.</span>
      </div>
    </div>
  );
}

function ConditionsPanel({ g, analysis, cases = [], website = false, readOnly, onChanged, setErr }) {
  const conds = (analysis && analysis.conditions) || [];
  const [openKey, setOpenKey] = useState(null);
  const areas = (analysis && analysis.areas ? analysis.areas : []).filter((a) => !a.universeLevel).map((a) => a.area);
  const canAdd = website && !readOnly && !g.report_sent_at && areas.length > 0;
  if (!conds.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{website ? 'Findings' : 'Conditions'}</span>
        <div className="mt-1 text-[11.5px] text-slate-500">{website ? 'No findings yet. A finding appears here when a reviewed row answers No to a requirement; one requirement not met is one finding, however many rows it touches.' : 'No conditions yet. A condition appears here when a reviewed case answers No to a requirement, a universe fails timeliness, or a universe is an Invalid Data Submission.'}</div>
        {canAdd && <div className="mt-2"><AddObservation g={g} areas={areas} cases={cases} onChanged={onChanged} setErr={setErr} /></div>}
      </div>
    );
  }
  // An Observation needs no cause or effect (CMS prints it as a short paragraph).
  const missing = conds.filter((c) => !c.overturned && c.classification !== 'observation' && (!String(c.cause || '').trim() || !String(c.effect || '').trim())).length;
  const grouped = analysis && analysis.totals && analysis.totals.grouped;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{website ? 'Findings' : 'Conditions and observations'} · {website && grouped ? grouped.total : conds.filter((c) => !c.overturned).length}</span>
        <span className="text-[11px] text-slate-500">{website
          ? 'One requirement not met is one finding, stated once with the rows it affects and their share of the rows reviewed. Rows are the impact, never separate failures. Classify each finding and write the cause, the effect and the corrective action.'
          : 'One requirement not met, with the sample cases under it. Classify each one and write the cause, the effect and the corrective action; they print in the report as CMS writes them.'}</span>
        {missing > 0 && !readOnly && <span className="ml-auto px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-amber-50 text-amber-800 ring-1 ring-amber-200">{missing} without a cause or effect</span>}
      </div>
      <div className="mt-2 space-y-2">
        {conds.map((c) => (
          <ConditionCard key={c.key} g={g} c={c} siblings={conds} website={website} readOnly={readOnly} open={openKey === c.key} onToggle={() => setOpenKey(openKey === c.key ? null : c.key)} onChanged={onChanged} setErr={setErr} rows={cases.filter((x) => x.section === c.area && !x.not_reviewed_at)} />
        ))}
      </div>
      {canAdd && <div className="mt-2"><AddObservation g={g} areas={areas} cases={cases} onChanged={onChanged} setErr={setErr} /></div>}
    </div>
  );
}

function ConditionCard({ g, c, siblings = [], website = false, rows = [], readOnly, open, onToggle, onChanged, setErr }) {
  const cls = { observation: 'Observation', car: 'CAR', ids: 'IDS' };
  // An observation written by hand (website audit): its statement, its rows
  // and what the organization should do are edited here; it is always an
  // Observation and is removed, not reclassified, before the draft issues.
  const manual = c.kind === 'manual';
  const caseWord = website ? 'Rows' : 'Sample cases';
  // "Same finding as" (operator order 2026-10-16): the standing findings of
  // the same element and classification this one may be grouped under. The
  // report then states one condition with the description once and this
  // finding under it as i.a, i.b; the response link still answers each one.
  const groupTargets = siblings.filter((s) => s.key !== c.key && !s.overturned && s.area === c.area && s.classification === c.classification && s.groupWith !== c.key);
  const clsColor = { observation: 'bg-sky-50 text-sky-700 ring-sky-200', car: 'bg-rose-50 text-rose-700 ring-rose-200', ids: 'bg-amber-50 text-amber-800 ring-amber-200' };
  const save = async (patch) => {
    const path = manual ? `/internal-audits/${g.id}/observations/${encodeURIComponent(c.key)}` : `/internal-audits/${g.id}/conditions/${encodeURIComponent(c.key)}`;
    try { setErr(''); await jfetch(path, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) }); onChanged(); }
    catch (e) { setErr(e.message); }
  };
  const remove = async () => {
    if (!window.confirm('Remove this observation from the audit?')) return;
    try { setErr(''); await jfetch(`/internal-audits/${g.id}/observations/${encodeURIComponent(c.key)}`, { method: 'DELETE' }); onChanged(); }
    catch (e) { setErr(e.message); }
  };
  const rowIds = new Set((c.cases || []).map((k) => String(k.caseId)));
  const toggleRow = (id) => save({ case_ids: rowIds.has(String(id)) ? [...rowIds].filter((x) => x !== String(id)) : [...rowIds, String(id)] });
  const isObs = c.classification === 'observation';
  const done = isObs || (String(c.cause || '').trim() && String(c.effect || '').trim());
  const area = 'w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 disabled:bg-slate-50';
  return (
    <div className={`border rounded-xl px-3.5 py-2.5 ${c.overturned ? 'border-slate-100 opacity-70' : 'border-slate-200'}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[12px] font-bold text-slate-900">{c.overturned ? 'Removed on rebuttal' : (c.ref || `Condition ${c.no}`)}</span>
        <span className="text-[11.5px] text-slate-600 truncate flex-1 min-w-[10rem]" title={manual ? c.requirement : c.title}>{manual ? `${c.area}, ${c.requirement}` : c.title}</span>
        {c.overturned
          ? <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 ring-1 ring-slate-200">Not in the final report</span>
          : <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold ring-1 ${clsColor[c.classification] || ''}`}>{cls[c.classification] || c.classification}</span>}
        {!c.overturned && <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${done ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'}`}>{done ? 'Written' : 'Cause or effect missing'}</span>}
        <span className="text-[10.5px] text-slate-400" title={c.statement?.met || ''}>{c.cases?.length ? (c.statement?.short || `${c.cases.length} of ${c.reviewed} cases`) : (c.universe ? `universe ${c.universe}` : (manual ? 'the element as a whole' : ''))}</span>
        <button onClick={onToggle} className="text-slate-300 hover:text-slate-500 text-xs">{open ? '▴' : '▾'}</button>
      </div>
      {open && manual && (
        <div className="mt-2 space-y-1.5">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Observation
            <textarea defaultValue={c.requirement || ''} disabled={readOnly} rows={2} className={`mt-0.5 ${area}`}
              onBlur={(e) => e.target.value.trim() !== (c.requirement || '') && save({ statement: e.target.value })} />
          </label>
          {rows.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Rows it points at</div>
              <div className="mt-1 max-h-32 overflow-y-auto flex flex-wrap gap-1.5">
                {rows.map((r) => (
                  <label key={r.id} className={`text-[11px] px-2 py-0.5 rounded-lg ring-1 ${readOnly ? '' : 'cursor-pointer'} ${rowIds.has(String(r.id)) ? 'bg-sky-100 text-sky-900 ring-sky-300' : 'bg-white text-slate-600 ring-slate-200'}`}>
                    <input type="checkbox" className="mr-1 align-middle" disabled={readOnly} checked={rowIds.has(String(r.id))} onChange={() => toggleRow(r.id)} />{rowName(r)}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="text-[11px] text-slate-500"><b>Authority:</b> {(c.criteria || []).join('; ')}</div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">The organization should
            <textarea defaultValue={c.correctiveAction || ''} disabled={readOnly} rows={2} placeholder="Leave blank for: monitor this area to ensure ongoing compliance" className={`mt-0.5 ${area}`}
              onBlur={(e) => e.target.value !== (c.correctiveAction || '') && save({ corrective_action: e.target.value })} />
          </label>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-sky-700">Written by hand on this element. It prints under Observations, the areas acknowledge it on the response link, and no corrective action plan is required.</span>
            {!readOnly && !g.report_sent_at && <button type="button" onClick={remove} className="ml-auto text-[10.5px] font-semibold text-slate-400 hover:text-rose-600 flex-none">Remove</button>}
          </div>
          {c.response && c.response.position && <div className="text-[11px] text-slate-500">Response received: {c.response.position.replace('_', ' ')}{c.response.disposition ? `, dispositioned (${c.response.disposition.replace('_', ' ')})` : ''}. Dispositions are recorded on the Responses tab.</div>}
        </div>
      )}
      {open && !manual && (
        <div className="mt-2 space-y-1.5">
          <div className="text-[11.5px] text-slate-700"><b>Requirement not met:</b> {c.requirement}</div>
          <div className="text-[11px] text-slate-500"><b>{website ? 'Authority' : 'Criteria'}:</b> {(c.criteria || []).join('; ')}</div>
          {c.cases?.length > 0 && <div className="text-[11px] text-slate-500"><b>{caseWord}{c.statement?.short ? ` (${c.statement.short})` : ''}:</b> {c.cases.map((k) => `${k.ref}${k.universe ? ` (${k.universe})` : ''}${k.comments ? `: ${k.comments}` : ''}`).join('; ')}</div>}
          {!c.overturned && c.kind !== 'ids' && <ClassificationButtons c={c} readOnly={readOnly} save={save} defaultKind={c.observation ? 'observation' : /integrity/i.test(c.area || '') ? 'ids' : 'car'} />}
          {!c.overturned && (groupTargets.length > 0 || c.groupWith) && (
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Same finding as
              <select value={c.groupWith || ''} disabled={readOnly} className={`mt-0.5 ${area}`} onChange={(e) => save({ group_with: e.target.value || null })}>
                <option value="">{c.groupSize > 1 ? `Grouped by its description under ${c.groupRef} (${c.groupSize} requirements)` : 'No. This finding stands on its own description'}</option>
                {groupTargets.map((s) => <option key={s.key} value={s.key}>{s.ref || `Condition ${s.no}`}: {s.title}{s.cases?.length ? ` (${s.cases.map((k) => k.ref).join(', ')})` : ''}</option>)}
              </select>
              <span className="block mt-0.5 text-[10.5px] font-normal normal-case tracking-normal text-slate-500">Findings with the same auditor description group on their own. Pick one here to group this finding under it: the report states one condition with the description once and each requirement not met under it (i.a, i.b); each is still answered separately on the response link.</span>
            </label>
          )}
          {isObs && <div className="text-[11px] text-sky-700">An Observation prints in the report as a short paragraph under OBSERVATIONS for its element, with the sample cases and what the organization should do. Cause and effect are optional.</div>}
          <div className="grid md:grid-cols-2 gap-2">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Cause
              <textarea defaultValue={c.cause || ''} disabled={readOnly} rows={2} placeholder="Why it happened: the process, system, training or staffing gap behind the failed cases" className={`mt-0.5 ${area}`}
                onBlur={(e) => e.target.value !== (c.cause || '') && save({ cause: e.target.value })} />
            </label>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Effect
              <textarea defaultValue={c.effect || ''} disabled={readOnly} rows={2} placeholder="What it did to the enrollee, the provider or the organization in the sample cases" className={`mt-0.5 ${area}`}
                onBlur={(e) => e.target.value !== (c.effect || '') && save({ effect: e.target.value })} />
            </label>
          </div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">{isObs ? 'The organization should' : 'Corrective action required'}
            <textarea defaultValue={c.correctiveAction || ''} disabled={readOnly} rows={2} placeholder={isObs ? 'For example: monitor that receipt dates are stamped on fax intake. Leave blank for the standard wording' : 'Leave blank to print the standard wording for this requirement'} className={`mt-0.5 ${area}`}
              onBlur={(e) => e.target.value !== (c.correctiveAction || '') && save({ corrective_action: e.target.value })} />
          </label>
          {c.response && c.response.position && <div className="text-[11px] text-slate-500">Response received: {c.response.position.replace('_', ' ')}{c.response.disposition ? `, dispositioned (${c.response.disposition.replace('_', ' ')})` : ''}. Dispositions are recorded on the Responses tab.</div>}
        </div>
      )}
    </div>
  );
}

function InternalReportPanel({ g, onChanged, setErr, pda = null }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [charts, setCharts] = useState(pda?.charts || null);
  const [blockers, setBlockers] = useState(pda?.blockers || []);
  const [draftBlockers, setDraftBlockers] = useState(pda?.draft_blockers || []);
  // The reconciliation check of the live results (operator order 2026-10-16):
  // { ok, checks, issues } from the server, stated with the letter.
  const [reconciliation, setReconciliation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showFinal, setShowFinal] = useState(false);
  const [toIds, setToIds] = useState(() => new Set());
  const [ccIds, setCcIds] = useState(() => new Set());
  // The letter on the screen is the one the audit keeps (operator order
  // 2026-10-16): the draft and the final are reviewed here, edited here, and
  // saved here; issuing takes the text as it stands. `mode` says which of the
  // two the panel holds, `savedText` what the record holds, so the panel can
  // say "Unsaved changes" and save on the way out.
  const [mode, setMode] = useState('draft');
  const [savedText, setSavedText] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  // The letter shows as the letter (operator order 2026-10-16): the same
  // letterhead, type and blocks the email and the Word file carry, edited in
  // place; "Plain text" is the same text, raw.
  const [view, setView] = useState('letter');
  const [letterhead, setLetterhead] = useState(null);
  useEffect(() => { jfetch(`/internal-audits/${g.id}/letterhead`).then(setLetterhead).catch(() => setLetterhead({ theme: null, logo_url: null })); }, [g.id]);
  const readOnly = g.status !== 'open';
  const isPda = g.program === 'pda';
  const dirty = open && draft.trim() !== String(savedText || '').trim();
  // The readiness list and the charts follow the audit as it changes (a saved
  // analysis or a disposition must enable the final without a manual refresh).
  useEffect(() => { if (pda) { setBlockers(pda.blockers || []); setDraftBlockers(pda.draft_blockers || []); setCharts(pda.charts || null); } }, [pda]);
  const fileBase = (kind) => `${kind} Audit Report ${String(g.program || '').toUpperCase()} ${g.cycle_label}`;
  // The website audit's screen reviews the draft NOTICE; the report itself
  // (report_full) rides as Word with the email and downloads from the hint line.
  const [reportFull, setReportFull] = useState('');
  const isWebsiteAudit = g.program === 'website';
  const loadDraft = async (regen) => {
    try {
      const d = await jfetch(`/internal-audits/${g.id}/report${regen ? '?regen=1' : ''}`);
      setDraft(d.report_draft || ''); setSavedText(d.stored ? d.report_draft || '' : null); setMode('draft'); setReportFull(d.report_full || '');
      setAnalysis(d.analysis || null); setCharts(d.charts || null); setBlockers(d.blockers || []); setDraftBlockers(d.draft_blockers || []); setReconciliation(d.reconciliation || null); setOpen(true);
    } catch (e) { setErr(e.message); }
  };
  // The final: the text the auditor saved, else a fresh build from the
  // results of record (regen always rebuilds).
  const loadFinal = async (regen) => {
    try {
      const d = await jfetch(`/internal-audits/${g.id}/report`);
      const stored = !regen && d.report_final_draft ? d.report_final_draft : null;
      setDraft(stored || d.final_preview || ''); setSavedText(stored); setMode('final');
      setAnalysis(d.analysis || null); setCharts(d.charts || null); setBlockers(d.blockers || []); setDraftBlockers(d.draft_blockers || []); setReconciliation(d.reconciliation || null); setOpen(true);
    } catch (e) { setErr(e.message); }
  };
  const save = async () => {
    if (readOnly || !dirty || draft.trim().length < 300) return false;
    setSaving(true);
    try {
      const r = await jfetch(`/internal-audits/${g.id}/report`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind: mode, report_text: draft }) });
      setSavedText(r.report_text || draft); setSavedAt(new Date()); setSaving(false); return true;
    } catch (e) { setErr(e.message); setSaving(false); return false; }
  };
  // Edits in the letter save themselves a beat after the last change.
  useEffect(() => {
    if (!open || readOnly || !dirty || saving || draft.trim().length < 300) return undefined;
    const t = setTimeout(() => { save(); }, 1500);
    return () => clearTimeout(t);
  }, [draft, open, readOnly, dirty, saving]); // eslint-disable-line react-hooks/exhaustive-deps
  // A protocol program draft issues only once every condition carries its
  // cause and effect (written on the Conditions panel above).
  const draftHeld = !isPda && !g.report_sent_at && draftBlockers.length > 0;
  const dl = async (kind, text) => {
    const isFinalText = !!g.report_final_at || mode === 'final';
    if (kind === 'pdf') {
      const { exportAsmtReportPdf, fetchLogoDataUrl } = await import('../FDR/exportAsmtReportPdf');
      let blocks = charts;
      if (isPda && !blocks) { try { const d = await jfetch(`/internal-audits/${g.id}/report`); blocks = d.charts || null; } catch { /* charts optional */ } }
      exportAsmtReportPdf({ text, fileBase: fileBase(isFinalText ? 'FINAL' : 'DRAFT'), sha256: g.report_final_sha256 || null, logoDataUrl: await fetchLogoDataUrl(`${API}/api/deck-studio/branding/logo`), analysis, charts: blocks });
    } else {
      // Word comes from the server renderer: the same file the emails carry.
      const title = `${isFinalText ? 'Final' : 'Draft'} Audit Report - ${String(g.program || '').toUpperCase()} ${g.cycle_label}`;
      await downloadLetterDocx(g.id, text, title).catch((e) => setErr(e.message));
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Audit report</span>
        <span className="flex items-center gap-1 text-[10.5px]">
          <span className={`px-1.5 py-px rounded-full font-semibold ${g.report_sent_at ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>{g.report_sent_at ? `✓ Draft issued ${new Date(g.report_sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : '1 · Draft'}</span>
          <span className="text-slate-200">·</span>
          <span className={`px-1.5 py-px rounded-full font-semibold ${g.report_final_at ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>{g.report_final_at ? `✓ Final ${new Date(g.report_final_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : '2 · Final'}</span>
        </span>
        <span className="ml-auto flex items-center gap-2">
          {!readOnly && !g.report_final_at && (
            <button onClick={() => loadDraft(!!g.report_sent_at)} className="text-[11.5px] font-semibold text-orange-700 hover:text-orange-900">
              {g.report_sent_at ? 'Refresh the draft from fieldwork' : 'Generate & review the draft'}
            </button>
          )}
          {!readOnly && g.report_sent_at && !g.report_final_at && (
            <button onClick={() => loadFinal(false)} className="text-[11.5px] font-semibold text-orange-700 hover:text-orange-900">
              Generate & review the final
            </button>
          )}
          {!readOnly && g.report_sent_at && !g.report_final_at && (
            <button disabled={busy || blockers.length > 0}
              title={blockers.length ? `Not ready: ${blockers[0]}` : 'Freeze the final report — the text as you reviewed it (else built from the results of record), sealed with its exhibit, emailed to the draft recipients (or the people you pick) with the compliance officers copied, and filed on Correspondence'}
              onClick={async () => {
                setBusy(true);
                try {
                  // What is on the screen is what issues; the server keeps the saved final otherwise.
                  const reviewed = open && mode === 'final' ? draft : '';
                  await jfetch(`/internal-audits/${g.id}/report/issue`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ final: true, report_text: reviewed, to_user_ids: [...toIds], cc_user_ids: [...ccIds] }) });
                  setOpen(false); onChanged();
                }
                catch (e) { setErr(e.message); if (e.data?.blockers) setBlockers(e.data.blockers); }
                setBusy(false);
              }}
              className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-[11.5px] font-semibold disabled:opacity-40">
              {busy ? 'Issuing…' : 'Issue the final report'}
            </button>
          )}
          {g.report_final_at && (
            <button onClick={async () => {
              if (!showFinal && !analysis && g.program === 'pda') {
                try { const d = await jfetch(`/internal-audits/${g.id}/report`); setAnalysis(d.analysis || null); } catch { /* charts optional */ }
              }
              setShowFinal((v) => !v);
            }} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-700">
              {showFinal ? 'Hide final' : 'View final'}
            </button>
          )}
        </span>
      </div>
      {reconciliation && (
        <div className={`mt-2 rounded-xl px-3.5 py-2 text-[11.5px] ${reconciliation.ok ? 'bg-emerald-50/70 ring-1 ring-emerald-200 text-emerald-900' : 'bg-rose-50/70 ring-1 ring-rose-200 text-rose-900'}`} title="Every standing finding prints once; every case under a condition is in its sample case list; cases failed, conditions, IDS and observations per element and in total equal the Results tab; the numbering is the one the response link carries">
          <span className="font-semibold">Reconciliation check: {reconciliation.ok ? `passed, ${reconciliation.checks} checks.` : `failed, ${reconciliation.issues.length} of ${reconciliation.checks} checks.`}</span>
          {reconciliation.ok ? <span> The numbers in the letter agree with each other and with the results of record{savedText != null && savedText !== draft ? '; a hand edited letter is checked when it is regenerated' : ''}.</span> : <span> {reconciliation.issues.slice(0, 4).join('. ')}.</span>}
        </div>
      )}
      {draftHeld && (
        <div className="mt-2 rounded-xl px-3.5 py-2.5 text-[11.5px] bg-amber-50/70 ring-1 ring-amber-200 text-amber-900">
          <div className="font-semibold">Before the draft can issue ({draftBlockers.length}): write the cause and the effect on each condition above.</div>
          {draftBlockers.slice(0, 6).map((b, i) => <div key={i} className="mt-0.5">· {b}</div>)}
          {draftBlockers.length > 6 && <div className="mt-0.5">· and {draftBlockers.length - 6} more</div>}
        </div>
      )}
      {pda && !g.report_final_at && (
        <div className={`mt-2 rounded-xl px-3.5 py-2.5 text-[11.5px] ${blockers.length ? 'bg-amber-50/70 ring-1 ring-amber-200 text-amber-900' : 'bg-emerald-50/70 ring-1 ring-emerald-200 text-emerald-900'}`}>
          <div className="font-semibold">{blockers.length ? `Before the final can issue (${blockers.length}):` : (isPda ? 'Ready: every listing is concluded, every response is dispositioned, and the analysis is written.' : 'Ready: every universe is tested, every case is concluded, and every condition is dispositioned.')}</div>
          {blockers.slice(0, 8).map((b, i) => <div key={i} className="mt-0.5">· {b}</div>)}
          {blockers.length > 8 && <div className="mt-0.5">· and {blockers.length - 8} more</div>}
        </div>
      )}
      {open && !g.report_final_at && (
        <div className="mt-2.5">
          <div className="flex items-center gap-2 flex-wrap text-[10.5px]">
            <span className="font-bold uppercase tracking-wider text-slate-400">{mode === 'final' ? 'Final report, for review' : isWebsiteAudit ? 'Draft report notice, for review' : 'Draft report, for review'}</span>
            {!readOnly && (
              <span className={`px-1.5 py-px rounded-full font-semibold ${dirty ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
                {saving ? 'Saving…' : dirty ? 'Unsaved changes' : savedAt ? `Saved ${savedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : savedText != null ? 'Saved' : 'Not saved yet'}
              </span>
            )}
            {!readOnly && !dirty && (
              <button onClick={() => (mode === 'final' ? loadFinal(true) : loadDraft(true))} className="text-slate-500 hover:text-slate-700 font-semibold">Regenerate from fieldwork</button>
            )}
            {!readOnly && dirty && <span className="text-slate-400">Save before regenerating; regenerating replaces this text.</span>}
            <span className="ml-auto inline-flex rounded-lg border border-slate-200 overflow-hidden text-[11px] font-semibold">
              <button onClick={() => setView('letter')} className={`px-2.5 py-1 ${view === 'letter' ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:text-slate-700'}`}>Letter</button>
              <button onClick={() => setView('text')} className={`px-2.5 py-1 ${view === 'text' ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:text-slate-700'}`}>Plain text</button>
            </span>
          </div>
          {mode === 'draft' && isWebsiteAudit && (
            <div className="mt-1.5 rounded-xl bg-sky-50/70 ring-1 ring-sky-100 px-3 py-2 text-[11.5px] text-slate-700 flex items-center gap-3 flex-wrap">
              <span>This notice is the email. The report itself goes with it as Word and the findings workbook as Excel; the areas answer on their link or in the workbook.</span>
              <button onClick={() => downloadLetterDocx(g.id, reportFull, fileBase('Draft')).catch((e) => setErr(e.message))} disabled={!reportFull} className="font-semibold text-orange-700 hover:text-orange-900 disabled:opacity-40">Draft report (Word)</button>
              <a href={`${API}/api/fdr/internal-audits/${g.id}/results.xlsx`} className="font-semibold text-orange-700 hover:text-orange-900">Findings workbook (Excel)</a>
            </div>
          )}
          {view === 'letter' ? (
            <div className="mt-1.5">
              <LetterEditor text={draft} onChange={setDraft} readOnly={readOnly} theme={letterhead?.theme || null} logoUrl={letterhead?.logo_url || null}
                kicker={mode === 'final' ? 'Final audit report' : isWebsiteAudit ? 'Draft audit report notice' : 'Draft audit report'} />
              {letterhead && (
                <div className="mt-1 text-[10.5px] text-slate-400 flex flex-wrap gap-x-4">
                  <span><span className="font-semibold">From</span> {letterhead.from_name}</span>
                  <span><span className="font-semibold">Subject</span> {letterhead[mode === 'final' ? 'final' : 'draft']?.subject}</span>
                  <span><span className="font-semibold">Attachment</span> {letterhead[mode === 'final' ? 'final' : 'draft']?.attachment}</span>
                  {!readOnly && <span>Click any line to edit it. Enter in a point adds the next one. Hover a block for + ¶ and ×.</span>}
                </div>
              )}
            </div>
          ) : (
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => { if (dirty) save(); }} rows={20} disabled={readOnly}
              className="mt-1.5 w-full text-[11.5px] font-mono border border-slate-300 rounded-xl px-3 py-2.5 leading-relaxed" />
          )}
          {!readOnly && (
            <div className="mt-2 grid md:grid-cols-2 gap-2.5">
              <OrgCcPicker label="To: the auditee (registered users)" ccIds={toIds} setCcIds={setToIds} />
              <OrgCcPicker label="Also copy (registered users)" ccIds={ccIds} setCcIds={setCcIds} />
            </div>
          )}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {!readOnly && (
              <button disabled={saving || !dirty || draft.trim().length < 300} onClick={save}
                title={mode === 'final' ? 'Keeps the final as reviewed; it issues from this text' : 'Keeps the draft as reviewed; it sends from this text'}
                className="px-3.5 py-2 rounded-xl bg-white border border-orange-300 hover:border-orange-500 text-orange-700 text-xs font-semibold disabled:opacity-50">
                {saving ? 'Saving…' : mode === 'final' ? 'Save the final' : 'Save the draft'}
              </button>
            )}
            {!readOnly && mode === 'draft' && (<>
              <button disabled={busy || draft.trim().length < 300 || !toIds.size || draftHeld}
                title={draftHeld ? `Not ready: ${draftBlockers[0]}` : 'Emails the draft to the auditee under the client logo, org users you picked on copy, you always copied — and records it on Correspondence'}
                onClick={async () => {
                  setBusy(true);
                  try { await jfetch(`/internal-audits/${g.id}/report/issue`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ report_text: draft, email: true, to_user_ids: [...toIds], cc_user_ids: [...ccIds] }) }); setOpen(false); onChanged(); } catch (e) { setErr(e.message); }
                  setBusy(false);
                }}
                className="px-3.5 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50">
                {busy ? 'Sending…' : 'Send the report to the auditee'}
              </button>
              <button disabled={busy || draft.trim().length < 300 || draftHeld}
                title={draftHeld ? `Not ready: ${draftBlockers[0]}` : 'You deliver it yourself — this records the draft as issued for management response, no email from the platform'}
                onClick={async () => {
                  setBusy(true);
                  try { await jfetch(`/internal-audits/${g.id}/report/issue`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ report_text: draft, to_user_ids: [...toIds], cc_user_ids: [...ccIds] }) }); setOpen(false); onChanged(); } catch (e) { setErr(e.message); }
                  setBusy(false);
                }}
                className="px-3.5 py-2 rounded-xl bg-white border border-slate-300 hover:border-slate-400 text-slate-600 text-xs font-semibold disabled:opacity-50">
                Mark as issued outside the platform
              </button>
            </>)}
            <button onClick={async () => { if (dirty) await save(); setOpen(false); }} className="px-3.5 py-2 rounded-xl bg-white border border-slate-300 text-slate-600 text-xs font-semibold">Close</button>
            <span className="ml-auto flex items-center gap-3">
              <button onClick={() => dl('word', draft)} disabled={draft.trim().length < 300} className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-40">Download Word</button>
              <button onClick={() => dl('pdf', draft)} disabled={draft.trim().length < 300} className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-40">Download PDF</button>
              {(g.program === 'pda' || isWebsiteAudit) && <a href={`${API}/api/fdr/internal-audits/${g.id}/results.xlsx`} className="text-[11px] font-semibold text-slate-500 hover:text-slate-700">{isWebsiteAudit ? 'Findings workbook (Excel)' : 'Results workbook (Excel)'}</a>}
            </span>
          </div>
        </div>
      )}
      {showFinal && g.report_final_at && (
        <div className="mt-2.5">
          <div className="max-h-[40rem] overflow-y-auto">
            <LetterEditor text={g.report_final || ''} onChange={() => {}} readOnly theme={letterhead?.theme || null} logoUrl={letterhead?.logo_url || null} kicker="Final audit report" />
          </div>
          <div className="mt-1.5 flex items-center gap-3">
            <span className="text-[10px] text-slate-400 font-mono">SHA-256 {String(g.report_final_sha256 || '').slice(0, 16)}…</span>
            <button onClick={() => dl('word', g.report_final)} className="ml-auto text-[11px] font-semibold text-slate-500 hover:text-slate-700">Download Word</button>
            <button onClick={() => dl('pdf', g.report_final)} className="text-[11px] font-semibold text-slate-500 hover:text-slate-700">Download PDF</button>
            {(g.program === 'pda' || isWebsiteAudit) && <a href={`${API}/api/fdr/internal-audits/${g.id}/results.xlsx`} className="text-[11px] font-semibold text-slate-500 hover:text-slate-700">{isWebsiteAudit ? 'Findings workbook (Excel)' : 'Results workbook (Excel)'}</a>}
          </div>
        </div>
      )}
    </div>
  );
}

function FieldworkTab({ data, g, readOnly, sections, onChanged, setErr }) {
  const [tab, setTab] = useState(sections[0] || '');
  const [uniOpen, setUniOpen] = useState(false);
  const [toolId, setToolId] = useState(null);
  const [uniTable, setUniTable] = useState('');
  const [ingestNote, setIngestNote] = useState('');
  const [qOpen, setQOpen] = useState(false);
  const [qText, setQText] = useState('');
  const [selectConfirm, setSelectConfirm] = useState(false);
  const [selectBusy, setSelectBusy] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeText, setRemoveText] = useState('');
  useEffect(() => {
    jfetch('/internal-audits/tools').then((d) => {
      const byName = (d.tools || []).find((t) => t.name === data.items[0]?.tool_name);
      setToolId((byName || (d.tools || [])[0])?.id || null);
    }).catch(() => {});
  }, [data.items]);
  const cases = (data.case_tests || []).filter((c) => c.section === tab);
  const questions = data.items.filter((i) => i.section === tab);
  const guideline = questions[0]?.guidance || '';
  // A question scoped to universe tables (a Table 3 claim takes the payment
  // questions) shows on the cases of those tables only.
  const applies = (q, c) => !(Array.isArray(q.universes) && q.universes.length) || q.universes.includes(String(c.universe_table || '').trim().toUpperCase());
  const scopeLabel = (q) => (Array.isArray(q.universes) && q.universes.length ? q.universes.join(', ') + ' only' : '');
  const uni = data.universe_requests || [];
  const uniTables = uni.filter((u) => u.source_kind !== 'file');
  const uniIn = uniTables.filter((u) => u.status === 'received').length;
  // A table with a live universe file (not a report, bytes present) is what
  // the selection needs; once the files are destroyed the button rests until
  // a new file is filed.
  const isReportDoc = (f) => /^Scrubber validation report|^Timeliness score report/.test(String(f.label || '')) || /^Scrubber_Validation_Report|^Timeliness_Scores_/.test(String(f.file_name || ''));
  const hasLiveUniverse = uniTables.some((u) => (u.status === 'received' || u.status === 'in_review') && (data.documents || []).some((f) => f.universe_request_id === u.id && !f.purged && !isReportDoc(f)));
  const codeOfLabel = (label) => (String(label || '').match(/\(([A-Z][A-Z_]+)\)\s*$/) || [])[1] || null;
  const ceilingFor = (name) => {
    const t = (data.program_tabs || []).find((x) => x.name === name);
    if (!t) return null;
    if (!t.per_universe) return t.target || null;
    const received = uniTables.filter((u) => (t.universes || []).includes(codeOfLabel(u.label)) && (u.status === 'received' || u.status === 'in_review')).length;
    return t.per_universe * received;
  };
  // CMS protocol programs conclude a case Compliant, Condition noted, or
  // Observation (noncompliance that needs no corrective action plan).
  const outcomes = OUTCOMES[tab] || (data.sample_observation ? [...OUTCOMES.default, ['observation', 'Observation', 'bg-sky-50 text-sky-700 ring-sky-200']] : OUTCOMES.default);

  return (
    <>
      <div className="mt-4 bg-white border border-slate-200 rounded-2xl px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Universes</span>
        <span className="text-[11.5px] text-slate-600">{uniTables.length === 0 ? (g.program === 'website' ? 'None. The sample is the published website: load the plans, pages, sites and tools on each tab.' : 'None. The sample is drawn from the published directory.') : <>{uniIn}/{uniTables.length} in. Record each universe's integrity test and timeliness result; three failed attempts make an Invalid Data Submission.{uni.length > uniTables.length ? ' Supplemental documentation is filed in its slot and read as received or not.' : ''}</>}</span>
        {!readOnly && (data.case_tests || []).some((c) => !c.purged) && !removeOpen && (
          <button onClick={() => { setRemoveOpen(true); setRemoveText(''); }}
            className={`${g.universe_destruction_attestation ? '' : 'ml-auto '}text-[11px] font-semibold text-rose-700 hover:text-rose-900`}
            title="Removes every sampled case on this audit so the selection can start over. Universe results, files, reports and the attestation stay.">
            Remove all samples
          </button>
        )}
        {removeOpen && (
          <span className="ml-auto flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-slate-700">Removes every sampled case; the selection starts over.</span>
            <input value={removeText} onChange={(e) => setRemoveText(e.target.value)} placeholder="Type REMOVE to confirm" className="text-[11px] border border-rose-200 rounded-lg px-2 py-1 w-40" />
            <button disabled={removeText.trim().toUpperCase() !== 'REMOVE'} onClick={async () => {
              try { setErr(''); const d = await jfetch(`/engagements/${g.id}/case-tests/remove-all`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ confirm: 'REMOVE' }) }); setIngestNote(`${d.removed} case${d.removed === 1 ? '' : 's'} removed. Select the samples again when the universes are in.`); setRemoveOpen(false); onChanged(); }
              catch (ex) { setErr(ex.message); }
            }} className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white text-xs font-semibold">Remove</button>
            <button onClick={() => setRemoveOpen(false)} className="text-[11px] font-semibold text-slate-500">Cancel</button>
          </span>
        )}
        {(data.case_tests || []).some((c) => !c.purged) && (
          <a href={`${API}/api/fdr/engagements/${g.id}/case-tests/export.xlsx`}
            className={`${g.universe_destruction_attestation || !readOnly ? '' : 'ml-auto '}px-3 py-1.5 rounded-lg border border-orange-300 text-orange-700 hover:border-orange-400 text-xs font-semibold no-underline`}
            title={g.program === 'website'
              ? 'One Excel file with the work: a tab per area with every plan, page, site and tool reviewed, the answer to each question, the outcome, the not reviewed reason, the auditor comments and the correction, then every question in full and every comment in one place.'
              : 'One Excel file with every selected case by area, the identifiers the business area needs to pull the files, and why each case was selected. Attach it to the email that goes out before the webinar. Contains member identifiers: internal use only.'}>
            {g.program === 'website' ? 'Export the work (Excel)' : 'Export the selection (Excel)'}
          </a>
        )}
        {g.universe_destruction_attestation && (
          <a href={`${API}/api/fdr/engagements/${g.id}/destruction-attestation.txt`}
            className={`${(data.case_tests || []).some((c) => !c.purged) ? '' : 'ml-auto '}px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10.5px] font-semibold no-underline`}
            title={`Universe files destroyed after sample selection on ${String(g.universe_destruction_attestation.attested_at || '').slice(0, 10)}; the attestation is on record.`}>
            Attestation · universes destroyed
          </a>
        )}
        {!readOnly && hasLiveUniverse && !selectConfirm && (
          <button onClick={() => setSelectConfirm(true)} className={`${(data.case_tests || []).some((c) => !c.purged) ? '' : 'ml-auto '}px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold`}
            title="The audit selects every element's sample per CMS-10717, moves the cases into their areas, then destroys the universe files and writes the attestation.">
            Select the samples
          </button>
        )}
        {!readOnly && selectConfirm && (
          <span className="ml-auto flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-slate-700">Selects the samples for every area, then destroys the universe files permanently and records the attestation. Continue?</span>
            <button disabled={selectBusy} onClick={async () => {
              if (selectBusy) return;
              try {
                setErr(''); setSelectBusy(true);
                const d = await jfetch(`/engagements/${g.id}/case-tests/draw-all`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
                setIngestNote(`${d.cases} case${d.cases === 1 ? '' : 's'} selected and moved into their areas; ${d.destroyed_files} universe file${d.destroyed_files === 1 ? '' : 's'} destroyed; attestation recorded. ${(d.sections || []).map((x) => `${x.section} — ${x.advisory}`).join(' ')}`);
                setSelectConfirm(false); onChanged();
              } catch (ex) { setIngestNote(''); setErr(ex.message); }
              setSelectBusy(false);
            }} className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-semibold">{selectBusy ? 'Working…' : 'Select and destroy'}</button>
            <button disabled={selectBusy} onClick={() => setSelectConfirm(false)} className="text-[11px] font-semibold text-slate-500">Cancel</button>
          </span>
        )}
        <button onClick={() => setUniOpen((v) => !v)} className={`${(!readOnly && uniIn > 0 && !g.universe_destruction_attestation) || g.universe_destruction_attestation ? '' : 'ml-auto '}text-[11.5px] font-semibold text-orange-700 hover:text-orange-900`}>
          {uniOpen ? 'Hide' : 'Open'}
        </button>
      </div>
      {uniOpen && (
        <div className="mt-2 bg-white border border-slate-200 rounded-2xl px-4 py-3 space-y-1.5">
          {uni.map((u) => {
            const files = (data.documents || []).filter((d) => d.universe_request_id === u.id);
            // One link per kind, newest each: the universe file, the error
            // report, the score report. Earlier filings stay in the record.
            const live = files.filter((f) => !f.purged).slice().sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
            const isReport = (f) => /^Scrubber validation report/.test(String(f.label || '')) || /^Scrubber_Validation_Report/.test(String(f.file_name || ''));
            const isScores = (f) => /^Timeliness score report/.test(String(f.label || '')) || /^Timeliness_Scores_/.test(String(f.file_name || ''));
            const report = live.find(isReport);
            const scoresDoc = live.find(isScores);
            const universeDoc = live.find((f) => !isReport(f) && !isScores(f));
            const older = live.length - (report ? 1 : 0) - (universeDoc ? 1 : 0) - (scoresDoc ? 1 : 0);
            const received = u.status === 'received' || u.status === 'in_review';
            const link = 'text-[10.5px] font-semibold text-orange-700 hover:text-orange-900 no-underline';
            return (
              <div key={u.id} className="py-1.5 border-t border-slate-100 first:border-t-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[12px] text-slate-700 truncate flex-1">{u.label}{u.record_count != null ? <span className="text-slate-400"> · {Number(u.record_count).toLocaleString()} records</span> : null}</span>
                  <span className={`px-1.5 py-px rounded-full text-[10px] font-semibold ${received ? 'bg-emerald-50 text-emerald-700' : u.status === 'resubmission_requested' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-400'}`}>
                    {received ? 'Received' : u.status === 'resubmission_requested' ? 'Resubmission requested' : 'Awaited'}
                  </span>
                  {u.source_kind === 'file'
                    ? <span className="text-[10px] font-semibold text-slate-400" title="A document, not a universe: received or not, never integrity or timeliness tested">Supplemental documentation</span>
                    : <>
                        {received && <span className="text-[10px] uppercase tracking-wide text-slate-400" title="The universe-level result you record after integrity testing">Recorded result</span>}
                        <UniverseTestControls u={u} readOnly={readOnly} onChanged={onChanged} setErr={setErr} />
                      </>}
                  {universeDoc && <a href={`${API}/api/fdr/documents/${universeDoc.id}/download`} title={universeDoc.file_name} className={link}>{u.source_kind === 'file' ? 'Download' : 'Universe'}</a>}
                  {!universeDoc && files.some((f) => f.purged && !isReport(f) && !isScores(f)) && (
                    <span className="px-1.5 py-px rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500" title="The universe file was destroyed after sample selection; its hash and count remain on record">Universe destroyed</span>
                  )}
                  {report && <a href={`${API}/api/fdr/documents/${report.id}/download`} title={`${report.file_name} — every error by row and type, no member data`} className={link}>Error report</a>}
                  {scoresDoc && <a href={`${API}/api/fdr/documents/${scoresDoc.id}/download`} title={`${scoresDoc.file_name} — timeliness by measure against the COA benchmark`} className={link}>Scores</a>}
                  {older > 0 && <span className="text-[10px] text-slate-400" title="Earlier filings remain in the audit record">+{older} earlier</span>}
                  {!readOnly && (
                    <label className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 cursor-pointer" title="File the universe into this slot">
                      Attach file
                      <input type="file" className="hidden" onChange={async (e) => {
                        const f = e.target.files?.[0]; e.target.value = '';
                        if (!f) return;
                        try {
                          const fd = new FormData(); fd.append('file', f);
                          await jfetch(`/engagements/universe-requests/${u.id}/upload`, { method: 'POST', body: fd });
                          onChanged();
                        } catch (ex) { setErr(ex.message); }
                      }} />
                    </label>
                  )}
                </div>
                {u.source_kind !== 'file' && u.scrub_result && <UniverseMeasures sr={u.scrub_result} />}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {sections.map((s) => {
          const n = (data.case_tests || []).filter((c) => c.section === s && !c.purged).length;
          const cap = ceilingFor(s);
          return (
            <button key={s} onClick={() => { setTab(s); setIngestNote(''); }}
              title={cap != null ? `${n} selected of ${cap} per the protocol` : `${n} selected`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${tab === s ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {s}{cap != null || n ? <span className={`ml-1.5 px-1.5 py-px rounded-full text-[10px] tabular-nums ${tab === s ? 'bg-white/20 text-white' : (cap != null && n > cap ? 'bg-rose-50 text-rose-700' : 'bg-white text-slate-500')}`}>{cap != null ? `${n} of ${cap}` : n}</span> : null}
            </button>
          );
        })}
        {!readOnly && (
          <span className="ml-auto flex items-center gap-2">
            {/* Each area tab IS its universe; only Data Integrity samples from all six. */}
            {NO_UNIVERSE_TABS.has(tab) ? (
              <span className="text-[10.5px] text-slate-400">{WEBSITE_ROW[tab] ? `Reviewed on the published website, one row per ${WEBSITE_ROW[tab][0]}` : 'Sample drawn from the published online directory'}</span>
            ) : !TAB_UNIVERSE[tab] ? (
              <select value={uniTable} onChange={(e) => setUniTable(e.target.value)}
                title="Which universe these samples were drawn from"
                className="text-[11px] border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-600">
                <option value="">Universe…</option>
                {(data?.audit?.tabs?.find((t) => t.name === tab)?.universes
                  || (data?.universe_requests || []).map((u) => (String(u.label || '').match(/\(([A-Z][A-Z_]+)\)\s*$/) || [])[1]).filter(Boolean)
                ).map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            ) : (
              <span className="text-[10.5px] text-slate-400">Universe: {TAB_UNIVERSE[tab]}</span>
            )}
            {!NO_UNIVERSE_TABS.has(tab) && (
              <button onClick={async () => {
                try {
                  setErr('');
                  const d = await jfetch(`/engagements/${g.id}/case-tests/draw`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ section: tab }) });
                  setIngestNote(`${d.cases} case${d.cases === 1 ? '' : 's'} selected per the protocol. ${d.advisory || ''}`); onChanged();
                } catch (ex) { setIngestNote(''); setErr(ex.message); }
              }} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                title="Selects this element's sample from the received universes the way CMS-10717 says, at random within the protocol's constraints. Add or remove cases by hand afterward as your judgment requires.">
                Select this element's sample
              </button>
            )}
            {NO_UNIVERSE_TABS.has(tab) && (
              <a href={WEBSITE_ROW[tab] ? `${API}/api/fdr/internal-audits/programs/website/listings-template.xlsx?tab=${encodeURIComponent(tab)}` : `${API}/api/fdr/internal-audits/programs/pda/listings-template.xlsx`}
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:border-slate-400 text-xs font-semibold no-underline"
                title="An Excel file with instructions and one column for each directory value: fill it from the published directory, then upload it here">
                Download the template
              </a>
            )}
            <label className="px-3 py-1.5 rounded-lg border border-orange-300 text-orange-700 hover:border-orange-400 text-xs font-semibold cursor-pointer"
              title={WEBSITE_ROW[tab] ? `One row per ${WEBSITE_ROW[tab][0]} from the published website, in the template layout or your own headers; every row becomes a ${WEBSITE_ROW[tab][0]} on this tab with its values shown on the card` : NO_UNIVERSE_TABS.has(tab) ? 'The listings from the published directory, one row each, in the template layout or your own headers; every row becomes a listing on this tab with its directory values filled in' : 'Your sample selection, in the universe record layout — every row becomes a case on this tab'}>
              {NO_UNIVERSE_TABS.has(tab) ? 'Upload the listings (Excel)' : 'Upload samples (Excel)'}
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={async (e) => {
                const f = e.target.files?.[0]; e.target.value = '';
                if (!f) return;
                try {
                  const fd = new FormData();
                  fd.append('file', f); fd.append('section', tab); fd.append('universe_table', NO_UNIVERSE_TABS.has(tab) ? '' : (TAB_UNIVERSE[tab] || uniTable));
                  const d = await jfetch(`/engagements/${g.id}/case-tests/import`, { method: 'POST', body: fd });
                  setErr(''); setIngestNote(`${d.cases} case${d.cases === 1 ? '' : 's'} added to ${tab} from your sample file.${d.advisory ? ` ${d.advisory}` : ''}`); onChanged();
                } catch (ex) { setIngestNote(''); setErr(ex.message); }
              }} />
            </label>
            <button onClick={async () => {
              try { await jfetch(`/engagements/${g.id}/case-tests`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ section: tab, universe_table: NO_UNIVERSE_TABS.has(tab) ? null : (TAB_UNIVERSE[tab] || uniTable || null) }) }); onChanged(); } catch (e) { setErr(e.message); }
            }} className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold"
              title={WEBSITE_ROW[tab] ? (QUESTION_REF_TABS.has(tab) ? `Add one ${WEBSITE_ROW[tab][0]}; the name and the link go on each question, when you have them` : `Add one ${WEBSITE_ROW[tab][0]} and type its name and URL on the card`) : NO_UNIVERSE_TABS.has(tab) ? 'Add one listing and type its directory values on the card' : 'Add one case by hand'}>
              {WEBSITE_ROW[tab] ? `+ ${WEBSITE_ROW[tab][1]}` : NO_UNIVERSE_TABS.has(tab) ? '+ Add a listing' : '+ Add a case'}
            </button>
          </span>
        )}
      </div>
      {ingestNote && <div className="mt-2 text-[11.5px] font-semibold text-emerald-700">{ingestNote}</div>}
      {guideline && <div className="mt-2 text-[11px] text-slate-500 bg-slate-50/70 border border-slate-200 rounded-xl px-3 py-2">{guideline}</div>}

      {/* The instrument itself, always on screen: what gets checked on every
          case in this element. Each sampled case answers these one by one.
          The Excel is one click away. */}
      <div className="mt-2 bg-white border border-slate-200 rounded-2xl px-4 py-3">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">The audit tool · what is reviewed on each case · {questions.length} questions</span>
          {toolId && (
            <a href={`${API}/api/fdr/internal-audits/tools/${toolId}/workbook`}
              className="ml-auto text-[11px] font-semibold text-orange-700 hover:text-orange-900 no-underline">
              Download the tool (Excel)
            </a>
          )}
        </div>
        {questions.map((q) => (
          <div key={q.id} className="flex items-start gap-2 py-1 border-t border-slate-50 first:border-t-0">
            <span className="text-[10px] font-bold text-orange-700 flex-none pt-0.5 w-7">{q.label_path}</span>
            <span className="text-[12px] text-slate-700 flex-1">{q.criteria_text}</span>
            {scopeLabel(q) && <span className="flex-none text-[10px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 mt-0.5">{scopeLabel(q)}</span>}
          </div>
        ))}
        {!readOnly && (
          <div className="flex items-center gap-2 pt-1.5 border-t border-slate-50">
            {qOpen ? (<>
              <input value={qText} onChange={(e) => setQText(e.target.value)} autoFocus
                placeholder="Write the question in plain language — it joins this tab and your version of the tool"
                className="flex-1 text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
              <button disabled={qText.trim().length < 10}
                onClick={async () => {
                  try {
                    await jfetch(`/engagements/${g.id}/questions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ section: tab, question: qText.trim() }) });
                    setQText(''); setQOpen(false); onChanged();
                  } catch (e) { setErr(e.message); }
                }}
                className="px-3 py-1.5 rounded-lg bg-orange-500 text-white text-[11px] font-semibold disabled:opacity-40">Add</button>
              <button onClick={() => { setQOpen(false); setQText(''); }} className="text-[11px] font-semibold text-slate-400 hover:text-slate-600">Cancel</button>
            </>) : (
              <button onClick={() => setQOpen(true)} className="text-[11px] font-semibold text-orange-700 hover:text-orange-900">+ Add a question</button>
            )}
          </div>
        )}
      </div>

      <div className="mt-3 space-y-2">
        {cases.map((c) => (
          <CaseRow key={c.id} c={c} questions={questions.filter((q) => applies(q, c))} outcomes={outcomes} readOnly={readOnly} onChanged={onChanged} setErr={setErr}
            draftIssued={!!g.report_sent_at}
            pda={g.program === 'pda' ? (data.pda?.analysis?.caseResults?.[String(c.id)] || {}) : null} website={g.program === 'website'} />
        ))}
        {!cases.length && <div className="text-[11.5px] text-slate-400 py-4 text-center">No cases sampled for {tab} yet. Add them per the guideline above.</div>}
      </div>
    </>
  );
}

function CaseRow({ c, questions, outcomes, readOnly, onChanged, setErr, pda = null, draftIssued = false, website = false }) {
  const [open, setOpen] = useState(false);
  const [asideOpen, setAsideOpen] = useState(false);
  const [asideReason, setAsideReason] = useState('');
  const answered = questions.filter((q) => (c.answers || {})[q.id]).length;
  const save = async (patch) => {
    try { await jfetch(`/engagements/case-tests/${c.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) }); onChanged(); } catch (e) { setErr(e.message); }
  };
  const outcomeCfg = outcomes.find((o) => o[0] === c.outcome);
  const isPda = !!pda;
  // Website Content and General Requirements: no name and link on the row;
  // each question carries its own, optional (operator order 2026-10-30).
  const perQuestion = website && QUESTION_REF_TABS.has(c.section);
  const qref = (q) => (c.question_refs && typeof c.question_refs === 'object' ? c.question_refs[String(q.id)] : null) || {};
  // NOT REVIEWED: the auditor sets the case aside with a reason. It stays in
  // the sample, greyed, out of every count, and never appears in the reports.
  const setAside = !!c.not_reviewed_at;
  return (
    <div className={`bg-white border rounded-2xl px-4 py-2.5 ${c.replaced || setAside ? 'border-slate-100 opacity-70' : 'border-slate-200'}`}>
      <div className="flex items-center gap-2.5 flex-wrap">
        <span className="text-[11px] font-bold text-slate-400 tabular-nums flex-none">#{c.case_no}</span>
        {!c.purged && !isPda && c.enrollee_ref ? (
          <span className="text-[11.5px] font-semibold text-slate-700 flex-none max-w-[220px] truncate" title="Enrollee, from the sample row">{c.enrollee_ref}</span>
        ) : null}
        {c.purged ? (
          <span className="text-[11.5px] text-slate-400 italic flex-1">Case identifiers erased at close</span>
        ) : isPda ? (
          <PdaListingFields c={c} readOnly={readOnly} save={save} />
        ) : perQuestion ? (
          c.case_ref
            ? <span className="text-[11.5px] font-semibold text-slate-700 flex-1 min-w-0 truncate" title={c.service_ref || ''}>{c.case_ref}</span>
            : <span className="text-[11px] text-slate-400 flex-1 min-w-0 truncate">Name and link are recorded on each question below</span>
        ) : website ? (<>
          <input defaultValue={c.case_ref || ''} disabled={readOnly} placeholder={WEBSITE_ROW[c.section]?.[2] || 'Name'} title="What was reviewed: the plan, the page, the site or the tool"
            onBlur={(e) => e.target.value !== (c.case_ref || '') && save({ case_ref: e.target.value })}
            className="w-64 text-xs border border-slate-200 rounded-lg px-2 py-1.5 disabled:bg-slate-50" />
          <input defaultValue={c.service_ref || ''} disabled={readOnly} placeholder={WEBSITE_ROW[c.section]?.[3] || 'URL'} title="The URL, or the contract and county for a plan"
            onBlur={(e) => e.target.value !== (c.service_ref || '') && save({ service_ref: e.target.value })}
            className="flex-1 min-w-[14rem] text-xs border border-slate-200 rounded-lg px-2 py-1.5 disabled:bg-slate-50" />
        </>) : (<>
          <input defaultValue={c.universe_table || ''} disabled={readOnly} placeholder="Universe (OD…)"
            onBlur={(e) => e.target.value !== (c.universe_table || '') && save({ universe_table: e.target.value })}
            className="w-28 text-xs border border-slate-200 rounded-lg px-2 py-1.5 disabled:bg-slate-50" />
          <input defaultValue={c.case_ref || ''} disabled={readOnly} placeholder="Case ID"
            onBlur={(e) => e.target.value !== (c.case_ref || '') && save({ case_ref: e.target.value })}
            className="w-32 text-xs border border-slate-200 rounded-lg px-2 py-1.5 disabled:bg-slate-50" />
          <input defaultValue={c.service_ref || ''} disabled={readOnly} placeholder="Service or issue"
            onBlur={(e) => e.target.value !== (c.service_ref || '') && save({ service_ref: e.target.value })}
            className="flex-1 min-w-[10rem] text-xs border border-slate-200 rounded-lg px-2 py-1.5 disabled:bg-slate-50" />
        </>)}
        {!readOnly && !c.purged && (
          <button onClick={async () => {
            try { setErr(''); await jfetch(`/engagements/case-tests/${c.id}`, { method: 'DELETE' }); onChanged(); } catch (e) { setErr(e.message); }
          }} className="text-[10.5px] font-semibold text-slate-400 hover:text-rose-600 flex-none" title="Remove this case from the sample">
            Remove
          </button>
        )}
        <span className="ml-auto flex items-center gap-1.5 flex-none">
          {setAside
            ? <span className="px-2 py-0.5 rounded-full text-[10.5px] font-semibold ring-1 bg-slate-100 text-slate-500 ring-slate-200" title={c.not_reviewed_reason || ''}>Not reviewed · not counted</span>
            : isPda
            ? (pda.outcome || pda.replaced ? <PdaCaseBadge r={pda} /> : <span className="text-[10.5px] text-slate-400">{answered}/{questions.length} answered</span>)
            : outcomeCfg
              ? <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold ring-1 ${outcomeCfg[2]}`}>{outcomeCfg[1]}</span>
              : <span className="text-[10.5px] text-slate-400">{answered}/{questions.length} answered</span>}
          <button onClick={() => setOpen((v) => !v)} className="text-slate-300 hover:text-slate-500 text-xs">{open ? '▴' : '▾'}</button>
        </span>
      </div>
      {open && setAside && (
        <div className="mt-2.5 flex items-center gap-2 flex-wrap rounded-xl bg-slate-50 ring-1 ring-slate-200 px-3.5 py-2.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex-none">Not reviewed</span>
          <span className="flex-1 text-[11.5px] text-slate-600">{c.not_reviewed_reason}</span>
          <span className="text-[10.5px] text-slate-400">{c.not_reviewed_at ? new Date(c.not_reviewed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
          {!readOnly && !draftIssued && (
            <button type="button" onClick={() => save({ not_reviewed_reason: '' })} className="text-[10.5px] font-semibold text-orange-700 hover:text-orange-900 flex-none">Put back in review</button>
          )}
        </div>
      )}
      {open && !setAside && (
        <div className="mt-2.5 space-y-1.5">
          {isPda && !c.purged && <PdaListingRecord c={c} readOnly={readOnly} save={save} />}
          {isPda && !c.purged && <PdaAttempts c={c} readOnly={readOnly} save={save} />}
          {!isPda && c.listing && !c.purged && (
            <div className="rounded-xl bg-sky-50/60 ring-1 ring-sky-200 px-3.5 py-2.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-sky-800 mb-1.5">What the record shows for this case — judge each question against this</p>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                {(Array.isArray(c.listing._columns)
                  ? c.listing._columns.filter((k) => k in c.listing && !k.startsWith('_')).map((k) => [k, c.listing[k]])
                  : Object.entries(c.listing).filter(([k]) => !k.startsWith('_'))).map(([label, value]) => (
                  <div key={label} className="flex items-baseline gap-2 text-[12px] min-w-0">
                    <span className="text-slate-500 flex-none max-w-[60%] break-words">{label}:</span>
                    {String(value ?? '').trim()
                      ? <span className="text-slate-800 font-medium min-w-0 break-words">{String(value)}</span>
                      : <span className="text-rose-600 font-medium italic">blank</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {questions.map((q) => {
            const val = (c.answers || {})[q.id];
            const r = perQuestion ? qref(q) : null;
            return (
              <div key={q.id} className="py-1 border-t border-slate-50">
                <div className="flex items-start gap-2">
                  <span className="text-[10px] font-bold text-slate-400 flex-none pt-0.5">{q.label_path}</span>
                  <span className="text-[12px] text-slate-700 flex-1">{q.criteria_text}</span>
                  <span className="flex items-center gap-1 flex-none">
                    {ANSWERS.map(([k, label, cls]) => (
                      <button key={k} disabled={readOnly}
                        onClick={() => save({ answers: { [q.id]: k } })}
                        className={`px-2 py-0.5 rounded-lg text-[10.5px] font-semibold ring-1 ${val === k ? cls : 'bg-white text-slate-400 ring-slate-200 hover:text-slate-600'}`}>
                        {label}
                      </button>
                    ))}
                  </span>
                </div>
                {perQuestion && (
                  <div className="flex items-center gap-2 mt-1 pl-6">
                    <input defaultValue={r.name || ''} disabled={readOnly} placeholder="Name (optional)" title="What was reviewed for this question: the page, the site, the section"
                      onBlur={(e) => e.target.value !== (r.name || '') && save({ question_refs: { [q.id]: { name: e.target.value } } })}
                      className="w-56 text-[11px] border border-slate-200 rounded-lg px-2 py-1 disabled:bg-slate-50" />
                    <input defaultValue={r.url || ''} disabled={readOnly} placeholder="Link (optional)" title="The address reviewed for this question"
                      onBlur={(e) => e.target.value !== (r.url || '') && save({ question_refs: { [q.id]: { url: e.target.value } } })}
                      className="flex-1 min-w-[12rem] text-[11px] border border-slate-200 rounded-lg px-2 py-1 disabled:bg-slate-50" />
                    {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="text-[10.5px] font-semibold text-orange-700 hover:text-orange-900 flex-none">Open</a>}
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex items-center gap-2 pt-1 border-t border-slate-50">
            {isPda ? (<>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex-none">Result</span>
              <PdaCaseBadge r={pda} />
              {pda.deficiencies && pda.deficiencies.length > 1 && <span className="text-[10.5px] text-slate-500">also {pda.deficiencies.filter((d) => d.label !== pda.worst?.label).map((d) => `${d.label} (${d.weight})`).join('; ')}</span>}
            </>) : (<>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex-none">Case outcome</span>
              {outcomes.map(([k, label, cls]) => (
                <button key={k} disabled={readOnly}
                  onClick={() => save({ outcome: c.outcome === k ? null : k })}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold ring-1 ${c.outcome === k ? cls : 'bg-white text-slate-400 ring-slate-200 hover:text-slate-600'}`}>
                  {label}
                </button>
              ))}
              {!readOnly && !draftIssued && !asideOpen && (
                <button type="button" onClick={() => { setAsideOpen(true); setAsideReason(''); }}
                  title="Set this case aside: it is not reviewed, it drops out of every count, and the reports never mention it. The record keeps it with your reason."
                  className="px-2.5 py-1 rounded-lg text-[11px] font-semibold ring-1 bg-white text-slate-400 ring-slate-200 hover:text-slate-600">
                  Not reviewed
                </button>
              )}
            </>)}
            <input defaultValue={c.comments || ''} disabled={readOnly} placeholder={isPda ? 'What the office said, in the office\'s words (prints as What was found)' : 'Auditor comments'}
              onBlur={(e) => e.target.value !== (c.comments || '') && save({ comments: e.target.value })}
              className="flex-1 min-w-[10rem] text-xs border border-slate-200 rounded-lg px-2 py-1.5 disabled:bg-slate-50" />
          </div>
          {isPda && c.outcome === 'condition' && !c.overturned_at && (
            <div className="flex items-center gap-2 pt-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 flex-none" title="Prints in the final report as how the deficiency was fixed">How it was fixed</span>
              <input defaultValue={c.correction || ''} disabled={readOnly}
                placeholder="What was corrected in the directory, by whom, and when"
                onBlur={(e) => e.target.value !== (c.correction || '') && save({ correction: e.target.value })}
                className="flex-1 min-w-[10rem] text-xs border border-amber-200 bg-amber-50/40 rounded-lg px-2 py-1.5 disabled:bg-slate-50" />
              {c.corrected_at && <span className="text-[10px] text-emerald-700 flex-none">✓ {new Date(c.corrected_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
              <span className="text-[10.5px] text-slate-400 flex-none">Rebuttals are handled on the Responses tab.</span>
            </div>
          )}
          {!isPda && ['condition', 'questioned', 'observation'].includes(c.outcome) && !c.overturned_at && (
            <div className="flex items-center gap-2 pt-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 flex-none" title="The report prints this next to the deficiency; the final report shows it as how the deficiency was fixed">How it was fixed</span>
              <input defaultValue={c.correction || ''} disabled={readOnly}
                placeholder="What was corrected, by whom, and when (prints in the report)"
                onBlur={(e) => e.target.value !== (c.correction || '') && save({ correction: e.target.value })}
                className="flex-1 min-w-[10rem] text-xs border border-amber-200 bg-amber-50/40 rounded-lg px-2 py-1.5 disabled:bg-slate-50" />
              {c.corrected_at && <span className="text-[10px] text-emerald-700 flex-none">✓ {new Date(c.corrected_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
            </div>
          )}
          {asideOpen && !isPda && (
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex-none">Not reviewed</span>
              <input value={asideReason} onChange={(e) => setAsideReason(e.target.value)} autoFocus
                placeholder="Why this case is not reviewed (for the record; never printed in the report)"
                className="flex-1 min-w-[14rem] text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
              <button type="button" disabled={asideReason.trim().length < 5} onClick={async () => { await save({ not_reviewed_reason: asideReason.trim() }); setAsideOpen(false); }}
                className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-slate-700 text-white disabled:opacity-40">Set aside</button>
              <button type="button" onClick={() => setAsideOpen(false)} className="text-[11px] font-semibold text-slate-400 hover:text-slate-600">Cancel</button>
            </div>
          )}
          {!isPda && ['condition', 'questioned', 'observation'].includes(c.outcome) && !c.overturned_at && (
            <div className="text-[10.5px] text-slate-400 pt-1">The condition or observation this case falls under, its classification, cause and effect are written on Results, under Conditions. Rebuttals are handled on the Responses tab.</div>
          )}
          {['condition', 'questioned', 'observation'].includes(c.outcome) && c.overturned_at && (
            <div className="flex items-center gap-2 pt-1">
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 ring-1 ring-slate-200 flex-none">Overturned · not a finding</span>
              <span className="flex-1 text-[10.5px] text-slate-500 truncate" title={c.overturn_reason || ''}>{c.overturn_reason}</span>
              {!readOnly && (
                <button type="button" className="text-[10.5px] font-semibold text-rose-600 hover:text-rose-700 flex-none"
                  title="Reinstate this deficiency as a finding"
                  onClick={() => { if (window.confirm('Reinstate this deficiency as a finding?')) save({ overturn_reason: '' }); }}>
                  Reinstate
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
